import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import heic2any from 'heic2any';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TablePagination,
  Tooltip,
  Checkbox,
  Paper,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  List,
  ListItem,
  ListItemText,
  Skeleton,
  Button,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  LinearProgress,
  InputAdornment,
  Snackbar
} from '@mui/material';
import {
  CloudUpload,
  Delete,
  Edit,
  Download,
  Visibility,
  Search,
  FilterList,
  LocationOn,
  Camera,
  Image as ImageIcon,
  Photo as PhotoIcon,
  ExpandMore,
  ExpandLess,
  Close,
  PhotoLibrary,
  Map,
  CalendarToday,
  Info,
  Psychology,
  CheckBox,
  CheckBoxOutlineBlank,
  Folder,
  Face,
  CloudOutlined,
  PersonPin,
  AccountCircle,
  Add,
  Save,
  Cancel,
  DeleteForever
} from '@mui/icons-material';

// Services and utilities
import imageService from '../api/imageService';
import sessionService from '../api/sessionService';
import { useFavicon } from '../useFavicon';
import { useAuth } from '../contexts/AuthContext';
import SessionSelectionDialog from '../components/SessionSelectionDialog';
import AuthenticatedImage from '../components/AuthenticatedImage';
import GooglePhotosDialog from '../components/GooglePhotosDialog';
import FaceRecognitionMenu from '../components/FaceRecognitionMenu';
import SimpleProgressDisplay from '../components/SimpleProgressDisplay';
import FaceRecognitionCard from '../components/FaceRecognitionCard';
import SceneTaggingDialogImproved from '../components/SceneTaggingDialogImproved';
import ImageSimilarityDialog from '../components/ImageSimilarityDialog';
import SessionEventTypeManager, { DEFAULT_EVENT_TYPES } from '../components/SessionEventTypeManager';
import { getServerUrl } from '../config/runtimeConfig';
import '../App.css';

// Define constants
const API_BASE_URL = getServerUrl();
const IMAGE_FAVICON_URL = process.env.PUBLIC_URL + "/image-upload.svg";

// TEMPORARY: Disable Application Insights for Google Photos debugging
// Create a mock logger that doesn't interfere with Google Photos API
const logger = {
  info: (...args) => console.log('[ImageUploadPage] INFO:', ...args),
  warn: (...args) => console.warn('[ImageUploadPage] WARN:', ...args),
  error: (...args) => console.error('[ImageUploadPage] ERROR:', ...args),
  debug: (...args) => console.log('[ImageUploadPage] DEBUG:', ...args)
};

const MAX_PREVIEWS_PER_SELECTION = 40;

console.log('🔧 ImageUploadPage - Using mock logger to avoid Application Insights interference with Google Photos API');
console.log('🔄 CODE VERSION: Face Matches Debug v2.1 - Feb 14, 2026');

// Utility functions
const formatFileSize = (bytes) => {
  if (!bytes || bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

const formatDate = (dateString) => {
  if (!dateString) return 'Unknown';
  try {
    return new Date(dateString).toLocaleString();
  } catch (error) {
    return 'Invalid Date';
  }
};

// HEIC Preview Generation Helper
const generateHeicPreview = async (file) => {
  try {
    console.log('🖼️ Generating HEIC preview for:', file.name);
    
    // Convert HEIC to JPEG for preview
    const convertedBlob = await heic2any({
      blob: file,
      toType: 'image/jpeg',
      quality: 0.7 // Lower quality for faster preview generation
    });
    
    // Create object URL from converted blob
    const previewUrl = URL.createObjectURL(convertedBlob);
    console.log('✅ HEIC preview generated successfully');
    
    return previewUrl;
  } catch (error) {
    console.warn('⚠️ Failed to generate HEIC preview:', error.message);
    return null; // Return null if conversion fails
  }
};

const ImageUploadPage = () => {
  // Get sessionId from URL params if provided
  const { sessionId: urlSessionId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  
    // State for delete all images in session
    const [deletingAllImages, setDeletingAllImages] = useState(false);

    // Handler for deleting all images in the current session
    const handleDeleteAllImagesInSession = async () => {
      if (!currentSession) return;
      if (!window.confirm(`Are you sure you want to delete ALL images in session "${currentSession.name}"? This cannot be undone.`)) return;
      setDeletingAllImages(true);
      try {
        await imageService.deleteAllImagesInSession(currentSession.uniqueId);
        // Reload images after deletion
        if (typeof loadImages === 'function') await loadImages();
        // Optionally refresh session stats
        if (typeof refreshCurrentSession === 'function') await refreshCurrentSession();
        alert('All images in this session have been deleted.');
      } catch (error) {
        alert('Failed to delete all images in session: ' + (error?.message || 'Unknown error'));
      } finally {
        setDeletingAllImages(false);
      }
    };
  // const { user } = useAuth(); // Currently unused but may be needed for future permission checks
  
  // Set favicon
  useFavicon(IMAGE_FAVICON_URL);

  // State management
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [previewFiles, setPreviewFiles] = useState([]);
  // Album event type is managed on the Session card (not per-upload)
  
  // Table and pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalImages, setTotalImages] = useState(0);
  const [sortBy, setSortBy] = useState('createdAt');
  
  // Filter and search state
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState({
    hasGPS: '',
    dateFrom: '',
    dateTo: '',
    tags: '',
    embeddingStatus: 'all' // 'all', 'embedded', 'not-embedded'
  });
  const [showFilters, setShowFilters] = useState(false);
  
  // Dialog states
  const [editDialog, setEditDialog] = useState({ open: false, image: null });
  const [editFormData, setEditFormData] = useState({ name: '', description: '', tags: '' });
  const [detailDialog, setDetailDialog] = useState({ open: false, image: null });
  const [embeddingDetails, setEmbeddingDetails] = useState(null);
  const [embeddingDetailsLoading, setEmbeddingDetailsLoading] = useState(false);
  const [embeddingDetailsError, setEmbeddingDetailsError] = useState('');
  const [deleteDialog, setDeleteDialog] = useState({ open: false, image: null, bulkDelete: false, selectedCount: 0 });
  const [deleting, setDeleting] = useState(false);
  
  // UI state
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [stats, setStats] = useState(null);
  
  // Image analysis state
  const [selectedImageIds, setSelectedImageIds] = useState([]);
  const [analysisDialog, setAnalysisDialog] = useState({ open: false, loading: false, results: null });
  const [analysisPrompt, setAnalysisPrompt] = useState('');
  const [analysisServiceStatus, setAnalysisServiceStatus] = useState(null);
  const [analysisProvider, setAnalysisProvider] = useState('openai');
  
  // Face recognition state
  const [faceRecognitionDialog, setFaceRecognitionDialog] = useState({ open: false, loading: false, results: null });
  const [includeAttributes, setIncludeAttributes] = useState(false);
  const [expandedFaceMatches, setExpandedFaceMatches] = useState({});

  // Scene tagging state
  const [sceneTaggingDialog, setSceneTaggingDialog] = useState({ open: false });

  // Image similarity state
  const [similarityDialog, setSimilarityDialog] = useState({ open: false });

  // Session state
  const [currentSession, setCurrentSession] = useState(null);
  const [sessionDialog, setSessionDialog] = useState({ open: false });
  const [sessionRequired, setSessionRequired] = useState(false); // Don't show session selection on first visit - user should come from sessions page
  const [sessionRefreshTrigger, setSessionRefreshTrigger] = useState(0); // Trigger to refresh session dialog

  // Google Photos state
  const [googlePhotosDialog, setGooglePhotosDialog] = useState({ open: false });

  // AWS Face Recognition state
  const [awsFaceDialog, setAwsFaceDialog] = useState({ 
    open: false, 
    loading: false, 
    results: null, 
    showProgress: false,
    progressData: {
      percentage: 0,
      current: 0,
      total: 0,
      status: 'Initializing...',
      isCompleted: false,
      hasError: false,
      results: null
    }
  });

  // Track if there's an in-progress operation (without auto-opening dialog)
  const [hasInProgressOperation, setHasInProgressOperation] = useState(false);
  const [stuckFaceOperation, setStuckFaceOperation] = useState(false); // 409 — previous run still in-progress in DB

  // AWS Face Search Options
  const [awsFaceOptions, setAwsFaceOptions] = useState({
    mainFacesOnly: true, // Default to main faces only
    showAllFaces: false, // Show all faces found, not just matches
    overrideProcessed: false // Override existing AWS processing results
  });

  // View image loading state
  const [viewImageLoading, setViewImageLoading] = useState(new Set());

  // Load images from API (filtered by session if selected)
  const loadImages = useCallback(async () => {
    // Don't load images if no session is selected
    if (!currentSession) {
      console.log('🔍 DEBUG: No session selected, skipping image load');
      setImages([]);
      setTotalImages(0);
      return;
    }

    try {
      setLoading(true);
      
      console.log('🔍 DEBUG: Loading images for session:', {
        sessionId: currentSession.uniqueId,
        sessionName: currentSession.name,
        page: page + 1,
        limit: rowsPerPage
      });
      
      logger.info('Loading images for session', {
        sessionId: currentSession.uniqueId,
        sessionName: currentSession.name
      });

      // Use session-specific image loading
      const response = await sessionService.getSessionImages(currentSession.uniqueId, {
        page: page + 1, // API expects 1-based pagination
        limit: rowsPerPage,
        sortBy,
        sortOrder: 'desc',
        search: searchQuery || undefined // Add search query
      });
      
      console.log('🔍 DEBUG: Raw API response:', {
        imageCount: response.images?.length || 0,
        totalCount: response.pagination?.totalCount || 0,
        firstImage: response.images?.[0] ? {
          id: response.images[0].uniqueId,
          name: response.images[0].name,
          isReferencePhoto: response.images[0].isReferencePhoto
        } : 'No images'
      });
      
      setImages(response.images || []);
      setTotalImages(response.pagination?.totalCount || 0);
      
      // Debug: Check which images have reference photo indicators
      if (response.images && response.images.length > 0) {
        const referenceImages = response.images.filter(img => img.isReferencePhoto);
        console.log('🔍 DEBUG: Reference photos found:', {
          total: response.images.length,
          referenceCount: referenceImages.length,
          referenceImages: referenceImages.map(img => ({
            id: img.uniqueId,
            name: img.name,
            isReferencePhoto: img.isReferencePhoto
          }))
        });
      }
      
      logger.info('Session images loaded successfully', {
        sessionId: currentSession.uniqueId,
        count: response.images?.length || 0,
        total: response.pagination?.totalCount || 0
      });

    } catch (error) {
      console.error('🔍 DEBUG: Error loading images:', error);
      logger.error('Failed to load session images', { 
        error: error.message,
        sessionId: currentSession?.uniqueId
      });
      showSnackbar('Failed to load images: ' + error.message, 'error');
    } finally {
      setLoading(false);
    }
  }, [currentSession, page, rowsPerPage, sortBy, searchQuery]);

  // Load statistics
  const loadStats = useCallback(async () => {
    try {
      const statsData = await imageService.getStats();
      setStats(statsData);
    } catch (error) {
      logger.warn('Failed to load statistics', { error: error.message });
    }
  }, []);

  // Load images on component mount and when filters change
  useEffect(() => {
    loadImages();
  }, [loadImages]);

  // Load stats only once on mount
  useEffect(() => {
    loadStats();
  }, []);

  // Show session selection on mount if no session is selected
  useEffect(() => {
    if (sessionRequired && !currentSession) {
      setSessionDialog({ open: true });
    }
  }, [sessionRequired, currentSession]);

  // Load session from URL parameter if provided
  useEffect(() => {
    const loadSessionFromUrl = async () => {
      if (urlSessionId && !currentSession) {
        try {
          const session = await sessionService.getSession(urlSessionId);
          setCurrentSession(session);
          setSessionRequired(false);
        } catch (error) {
          console.error('Failed to load session from URL:', error);
          showSnackbar('Failed to load session from URL', 'error');
          // Redirect to sessions page if session can't be loaded
          navigate('/sessions');
        }
      } else if (!urlSessionId && !currentSession) {
        // No session ID in URL and no current session - redirect to sessions page
        navigate('/sessions');
      }
    };
    
    loadSessionFromUrl();
  }, [urlSessionId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reload images when face recognition results are updated
  useEffect(() => {
    if (sessionRefreshTrigger > 0) {
      loadImages();
    }
  }, [sessionRefreshTrigger, loadImages]);

  // Check AWS Face Recognition operation status when session changes or on mount
  useEffect(() => {
    if (currentSession) {
      console.log('⚡ Session changed or component mounted, checking AWS operation status...');
      checkAwsOperationStatus();
    }
  }, [currentSession]); // eslint-disable-line react-hooks/exhaustive-deps

  const checkAwsOperationStatus = async () => {
    if (!currentSession) {
      console.log('⚠️ No current session, skipping AWS operation status check');
      return;
    }
    
    console.log('🔍 Checking AWS operation status for session:', currentSession.uniqueId);
    
    try {
      const statusData = await imageService.getAwsFaceRecognitionStatus(currentSession.uniqueId);
      
      console.log('📊 Status data received:', statusData);
      
      if (statusData.success && statusData.operationStatus) {
        const opStatus = statusData.operationStatus;
        
        console.log('📋 Operation status:', {
          inProgress: opStatus.inProgress,
          status: opStatus.status,
          current: opStatus.current,
          total: opStatus.total
        });
        
        // If operation is in progress, track it but DON'T auto-open the dialog
        if (opStatus.inProgress && opStatus.status === 'in-progress') {
          console.log('✅ Found in-progress AWS Face Recognition operation');

          // If stale (backend restarted mid-run), show Force Reset instead of progress banner
          if (opStatus.isStale) {
            console.log('⚠️ Operation is stale (backend restart detected) — showing Force Reset');
            setStuckFaceOperation(true);
            return;
          }
          
          // Track that there's an operation in progress
          setHasInProgressOperation(true);
          
          // Update the dialog state (but keep it closed)
          setAwsFaceDialog(prev => ({
            ...prev,
            open: false, // Don't auto-open
            loading: true,
            showProgress: true,
            results: null,
            progressData: opStatus.progressData || {
              percentage: 0,
              current: opStatus.current || 0,
              total: opStatus.total || 0,
              status: opStatus.progressData?.statusMessage || 'Resuming...',
              isCompleted: false,
              hasError: false,
              currentPhotoName: opStatus.progressData?.currentPhotoName,
              lastImageTime: opStatus.progressData?.lastImageTime || 0,
              averageTime: opStatus.progressData?.averageTime || 0,
              estimatedTimeRemaining: opStatus.progressData?.estimatedTimeRemaining || 0
            }
          }));
          
          // Reconnect to progress updates
          const progressWebSocket = (await import('../utils/ProgressWebSocket')).default;
          const operationId = `find-with-reference-persons-${currentSession.uniqueId}`;
          
          console.log('🔌 Subscribing to WebSocket updates for:', operationId);
          
          await progressWebSocket.subscribe(operationId, (data) => {
            console.log('📡 WebSocket update received:', data);
            setAwsFaceDialog(prev => {
              console.log('📊 Current dialog state:', prev);
              const newState = {
                ...prev,
                loading: true, // Ensure loading stays true
                showProgress: true, // Ensure showProgress stays true
                progressData: data
              };
              console.log('📊 New dialog state:', newState);
              return newState;
            });
            
            if (data.isCompleted || data.hasError) {
              console.log('🏁 Operation completed or failed');
              setTimeout(() => {
                progressWebSocket.unsubscribe(operationId);
                setAwsFaceDialog(prev => ({
                  ...prev,
                  loading: false,
                  showProgress: false
                }));
                setHasInProgressOperation(false);
              }, 1000);
            }
          });
          
          // Don't show snackbar - the banner will notify the user
        } else {
          // No operation in progress
          setHasInProgressOperation(false);
        }
      }
    } catch (error) {
      logger.error('Failed to check AWS operation status', { error: error.message });
      setHasInProgressOperation(false);
    }
  };

  // Function to view the in-progress operation
  const viewInProgressOperation = () => {
    console.log('👁️ Opening dialog to view in-progress operation');
    console.log('📊 Current awsFaceDialog state:', awsFaceDialog);
    console.log('📊 hasInProgressOperation:', hasInProgressOperation);
    setAwsFaceDialog(prev => {
      const newState = {
        ...prev,
        open: true
      };
      console.log('📊 Opening dialog with state:', newState);
      return newState;
    });
  };

  // TEMPORARY: Disable Application Insights fetch tracking when Google Photos dialog is open
  useEffect(() => {
    if (googlePhotosDialog.open) {
      console.log('🚫 Google Photos dialog opened - temporarily disabling Application Insights fetch tracking');
      
      // Store the current fetch function
      const currentFetch = window.fetch;
      
      // Override with a version that completely bypasses Application Insights for Google Photos
      window.fetch = async (input, init) => {
        const url = typeof input === 'string' ? input : input?.url;
        
        // If it's a Google Photos API call, use the original fetch without any tracking
        // Skip tracking for Google Photo Picker API requests
        if (url && (url.includes('photoslibrary.googleapis.com') || url.includes('googleapis.com/js/api.js') || url.includes('picker'))) {
          console.log('🔥 Bypassing ALL tracking for Google Photos API call:', url);
          
          // Find the original fetch function by looking for it in various places
          let originalFetch = currentFetch;
          
          // Try to find the truly original fetch
          if (window.__originalFetch) {
            originalFetch = window.__originalFetch;
          } else if (typeof currentFetch === 'function' && currentFetch.toString().includes('originalFetch')) {
            // If current fetch seems to be wrapped, try to extract original
            try {
              originalFetch = eval('originalFetch');
            } catch (e) {
              // Fallback to current fetch
            }
          }
          
          return originalFetch.call(window, input, init);
        }
        
        // For all other calls, use the current fetch (which may include Application Insights)
        return currentFetch.call(window, input, init);
      };
      
      // Cleanup function to restore original fetch when dialog closes
      return () => {
        console.log('✅ Google Photos dialog closed - restoring Application Insights fetch tracking');
        window.fetch = currentFetch;
      };
    }
  }, [googlePhotosDialog.open]);

  // Session selection handlers
  const handleSessionSelected = (session) => {
    setCurrentSession(session);
    setSessionRequired(false);
    setSessionDialog({ open: false });
    
    logger.info('Session selected', {
      sessionId: session.uniqueId,
      sessionName: session.name
    });
    
    showSnackbar(`Session "${session.name}" selected`, 'success');
  };

  const handleSessionDeleted = (deletedSession) => {
    // If the deleted session is the current session, clear it and show session dialog
    if (currentSession && currentSession.uniqueId === deletedSession.uniqueId) {
      setCurrentSession(null);
      setSessionRequired(true);
      // Keep the session dialog open so user can select another session
      
      logger.info('Current session was deleted, clearing session state', {
        deletedSessionId: deletedSession.uniqueId,
        deletedSessionName: deletedSession.name
      });
      
      showSnackbar(`Session "${deletedSession.name}" deleted. Please select another session.`, 'info');
    } else {
      logger.info('Session deleted', {
        deletedSessionId: deletedSession.uniqueId,
        deletedSessionName: deletedSession.name
      });
      
      showSnackbar(`Session "${deletedSession.name}" deleted successfully`, 'success');
    }
  };

  const handleChangeSession = () => {
    navigate('/sessions');
  };

  // Function to refresh current session data (to update image counts)
  const refreshCurrentSession = async () => {
    if (!currentSession) return;
    
    try {
      const updatedSession = await sessionService.getSession(currentSession.uniqueId);
      setCurrentSession(updatedSession);
      logger.info('Current session data refreshed', {
        sessionId: updatedSession.uniqueId,
        imageCount: updatedSession.formattedStats?.totalImages || 0
      });
    } catch (error) {
      logger.error('Failed to refresh current session data', { error: error.message });
    }
  };

  // Session-level event type is shown/edited in the Session card UI

  // Google Photos handler
  const handleGooglePhotosSelected = async (selectedPhotos) => {
    try {
      setUploading(true);
      setUploadProgress(0);
      
      logger.info('Starting Google Photos import', {
        sessionId: currentSession.uniqueId,
        photoCount: selectedPhotos.length
      });

      const totalPhotos = selectedPhotos.length;
      let uploadedCount = 0;

      for (const photo of selectedPhotos) {
        try {
          // Download the photo from Google Photos
          const response = await fetch(photo.baseUrl + '=d');
          if (!response.ok) {
            throw new Error(`Failed to download photo: ${response.statusText}`);
          }
          
          const blob = await response.blob();
          const file = new File([blob], photo.filename || `photo-${Date.now()}.jpg`, {
            type: blob.type || 'image/jpeg'
          });

          // Upload to session
          await sessionService.uploadImageToSession(currentSession.uniqueId, file, {
            name: photo.filename || file.name,
            description: photo.description || '',
            tags: 'google-photos,imported'
          });

          uploadedCount++;
          setUploadProgress(Math.round((uploadedCount / totalPhotos) * 100));
          
        } catch (error) {
          logger.error('Failed to import photo from Google Photos', {
            photoId: photo.id,
            error: error.message
          });
          showSnackbar(`Failed to import ${photo.filename}: ${error.message}`, 'warning');
        }
      }

      if (uploadedCount > 0) {
        showSnackbar(`Successfully imported ${uploadedCount} photos from Google Photos`, 'success');
        loadImages(); // Refresh the image list
        loadStats(); // Refresh stats
        
        // Refresh current session to update active session display
        await refreshCurrentSession();
        
        // Trigger session dialog refresh to update image counts
        setSessionRefreshTrigger(prev => prev + 1);
      }

    } catch (error) {
      logger.error('Google Photos import failed', { error: error.message });
      showSnackbar('Failed to import from Google Photos: ' + error.message, 'error');
    } finally {
      setUploading(false);
      setUploadProgress(0);
      setGooglePhotosDialog({ open: false });
    }
  };

  // Handler for auto-uploaded Google Photos (new approach)
  const handleGooglePhotosImported = async (uploadedFiles) => {
    try {
      logger.info('Google Photos auto-upload completed', {
        sessionId: currentSession.uniqueId,
        uploadedCount: uploadedFiles.length
      });

      // Show success message
      showSnackbar(`Successfully imported ${uploadedFiles.length} photos from Google Photos!`, 'success');
      
      // Refresh the image list to show the new photos
      await loadImages();
      
      // Refresh stats
      await loadStats();
      
      // Refresh current session to update active session display
      await refreshCurrentSession();
      
      // Trigger session dialog refresh to update image counts
      setSessionRefreshTrigger(prev => prev + 1);
      
    } catch (error) {
      logger.error('Failed to refresh after Google Photos import', { error: error.message });
      showSnackbar('Photos were imported but failed to refresh the list. Please refresh the page.', 'warning');
    }
  };

  // File drop zone configuration
  const onDrop = useCallback(async (acceptedFiles, rejectedFiles) => {
    logger.info('Files dropped', {
      accepted: acceptedFiles.length,
      rejected: rejectedFiles.length,
      acceptedNames: acceptedFiles.map(file => file.name),
      rejectedDetails: rejectedFiles.map(file => ({
        name: file.file?.name,
        errors: file.errors?.map(error => error.message) || []
      }))
    });

    if (rejectedFiles.length > 0) {
      const errors = rejectedFiles.map(file => 
        `${file.file.name}: ${file.errors.map(e => e.message).join(', ')}`
      );
      showSnackbar(`Some files were rejected: ${errors.join('; ')}`, 'warning');
    }

    if (acceptedFiles.length > 0) {
      logger.info('Queueing accepted files for preview generation', {
        fileCount: acceptedFiles.length,
        previewLimit: MAX_PREVIEWS_PER_SELECTION,
        fileDetails: acceptedFiles.map(file => ({
          name: file.name,
          type: file.type,
          size: file.size
        }))
      });

      setSelectedFiles(prev => [...prev, ...acceptedFiles]);

      const previewStartIndex = previewFiles.length;
      const filesToPreview = acceptedFiles.slice(0, MAX_PREVIEWS_PER_SELECTION);
      const skippedPreviewCount = acceptedFiles.length - filesToPreview.length;

      const queuedPreviews = acceptedFiles.map((file, index) => {
        const isHeic = file.type === 'image/heic' || file.type === 'image/heif' || 
                      file.name.toLowerCase().endsWith('.heic') || 
                      file.name.toLowerCase().endsWith('.heif') ||
                      file.name.toLowerCase().endsWith('.eic');

        return {
          file,
          preview: null,
          name: file.name,
          description: '',
          tags: '',
          isHeic,
          previewGenerated: false,
          previewDeferred: index >= MAX_PREVIEWS_PER_SELECTION,
        };
      });

      setPreviewFiles(prev => [...prev, ...queuedPreviews]);

      if (skippedPreviewCount > 0) {
        showSnackbar(
          `Queued ${acceptedFiles.length} files. Generated previews for the first ${filesToPreview.length}; ${skippedPreviewCount} more will upload without previews.`,
          'info'
        );
      }

      const generatedPreviews = await Promise.all(filesToPreview.map(async (file) => {
        const isHeic = file.type === 'image/heic' || file.type === 'image/heif' || 
                      file.name.toLowerCase().endsWith('.heic') || 
                      file.name.toLowerCase().endsWith('.heif') ||
                      file.name.toLowerCase().endsWith('.eic');

        const preview = isHeic ? await generateHeicPreview(file) : URL.createObjectURL(file);

        return {
          preview,
          previewGenerated: !!preview,
        };
      }));

      logger.info('Preview generation completed', {
        generatedPreviewCount: generatedPreviews.length,
        deferredPreviewCount: skippedPreviewCount,
      });

      setPreviewFiles(prev => {
        const next = [...prev];
        generatedPreviews.forEach((generatedPreview, index) => {
          const targetIndex = previewStartIndex + index;
          if (!next[targetIndex]) return;
          next[targetIndex] = {
            ...next[targetIndex],
            preview: generatedPreview.preview,
            previewGenerated: generatedPreview.previewGenerated,
            previewDeferred: false,
          };
        });
        return next;
      });
    } else {
      logger.warn('Drop event completed with no accepted files');
    }
  }, [previewFiles.length]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png', '.gif', '.webp', '.bmp', '.tiff', '.heic', '.heif', '.eic']
    },
    maxSize: 50 * 1024 * 1024, // 50MB
    multiple: true,
    onFileDialogOpen: () => {
      logger.info('Local file picker opened', {
        sessionId: currentSession?.uniqueId || null,
        sessionName: currentSession?.name || null
      });
    },
    onFileDialogCancel: () => {
      logger.warn('Local file picker closed without selecting accepted files');
    }
  });

  useEffect(() => {
    logger.info('Selected files queue changed', {
      count: selectedFiles.length,
      names: selectedFiles.map(file => file.name)
    });
  }, [selectedFiles]);

  useEffect(() => {
    logger.info('Preview files queue changed', {
      count: previewFiles.length,
      previews: previewFiles.map(file => ({
        name: file.name,
        previewGenerated: !!file.preview,
        isHeic: !!file.isHeic
      }))
    });
  }, [previewFiles]);

  // Remove file from selection
  const removeFile = (index) => {
    const newFiles = [...selectedFiles];
    const newPreviews = [...previewFiles];
    
    // Revoke object URL to prevent memory leak (only if preview exists)
    if (newPreviews[index].preview) {
      URL.revokeObjectURL(newPreviews[index].preview);
    }
    
    newFiles.splice(index, 1);
    newPreviews.splice(index, 1);
    
    setSelectedFiles(newFiles);
    setPreviewFiles(newPreviews);
  };

  // Update file metadata
  const updateFileMetadata = (index, field, value) => {
    const newPreviews = [...previewFiles];
    newPreviews[index][field] = value;
    setPreviewFiles(newPreviews);
  };

  // Upload images
  const handleUpload = async () => {
    logger.info('Upload button clicked', {
      selectedCount: selectedFiles.length,
      previewCount: previewFiles.length,
      sessionId: currentSession?.uniqueId || null,
      sessionName: currentSession?.name || null,
      uploading
    });

    if (selectedFiles.length === 0) {
      logger.warn('Upload blocked because no files are selected');
      showSnackbar('Please select at least one image to upload', 'warning');
      return;
    }

    if (!currentSession) {
      logger.warn('Upload blocked because no current session is selected');
      showSnackbar('Please select a session before uploading', 'warning');
      setSessionDialog({ open: true });
      return;
    }

    try {
      setUploading(true);
      setUploadProgress(0);

      logger.info('Starting image upload', {
        fileCount: selectedFiles.length,
        sessionId: currentSession.uniqueId,
        sessionName: currentSession.name
      });

      // Prepare metadata including sessionId
      const metadata = {
        sessionId: currentSession.uniqueId,
        names: previewFiles.map(p => p.name),
        descriptions: previewFiles.map(p => p.description),
        tags: previewFiles.map(p => p.tags)
      };
      // eventType is taken from the session settings on the backend

      const result = await imageService.uploadImages(
        selectedFiles,
        metadata,
        (progress) => {
          logger.info('Upload progress updated', {
            progress,
            fileCount: selectedFiles.length
          });
          setUploadProgress(progress);
        }
      );

      logger.info('Upload completed', result);

      // Clear selected files
      previewFiles.forEach(preview => {
        if (preview.preview) {
          URL.revokeObjectURL(preview.preview);
        }
      });
      setSelectedFiles([]);
      setPreviewFiles([]);
      setUploadProgress(0);

      // Show result
      if (result.failed > 0) {
        showSnackbar(
          `Upload completed: ${result.uploaded} successful, ${result.failed} failed`,
          'warning'
        );
      } else {
        showSnackbar(`Successfully uploaded ${result.uploaded} images`, 'success');
      }

      // Reload images and refresh session data
      await loadImages();
      await loadStats();
      
      // Refresh current session to update active session display
      await refreshCurrentSession();
      
      // Trigger session dialog refresh to update image counts
      setSessionRefreshTrigger(prev => prev + 1);

    } catch (error) {
      logger.error('Upload failed', { error: error.message });
      showSnackbar('Upload failed: ' + error.message, 'error');
    } finally {
      logger.info('Upload flow finished', {
        selectedCount: selectedFiles.length,
        previewCount: previewFiles.length
      });
      setUploading(false);
      setUploadProgress(0);
    }
  };

  // Handle image actions
  const handleEdit = useCallback((image) => {
    setEditFormData({
      name: image.name || '',
      description: image.description || '',
      tags: image.tags || ''
    });
    setEditDialog({ open: true, image });
  }, []);

  const handleView = useCallback((image) => {
    // Check both top-level and nested paths for faceDetection
    const faceDetection = image.faceDetection || image.faceRecognition?.faceDetection;
    
    console.log('🔍 [DEBUG] Opening image details:', {
      imageId: image.uniqueId,
      imageName: image.name,
      hasFaceRecognition: !!image.faceRecognition,
      hasFaceDetection_topLevel: !!image.faceDetection,
      hasFaceDetection_nested: !!image.faceRecognition?.faceDetection,
      faceDetectionProcessed: faceDetection?.processed,
      faceDetectionTotalFaces: faceDetection?.totalFaces,
      faceRecognitionMatches: image.faceRecognition?.matches?.length || 0,
      faceRecognitionAwsProcessed: image.faceRecognition?.awsProcessed
    });
    
    // Log the complete image structure for debugging
    console.log('📊 [DEBUG] Complete image object:', JSON.stringify(image, null, 2));
    
    setEmbeddingDetails(null);
    setEmbeddingDetailsError('');
    setDetailDialog({ open: true, image });
  }, []);

  useEffect(() => {
    const fetchEmbeddingDetails = async () => {
      if (!detailDialog.open || !detailDialog.image?.uniqueId) {
        return;
      }

      setEmbeddingDetailsLoading(true);
      setEmbeddingDetailsError('');

      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${API_BASE_URL}/api/embeddings/image/${detailDialog.image.uniqueId}/details`, {
          method: 'GET',
          headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {})
          },
          credentials: 'include'
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.error || errorData.message || `Failed with status ${response.status}`);
        }

        const data = await response.json();
        setEmbeddingDetails(data.embedding || null);
      } catch (error) {
        setEmbeddingDetailsError(error.message || 'Failed to load embedding details');
      } finally {
        setEmbeddingDetailsLoading(false);
      }
    };

    fetchEmbeddingDetails();
  }, [detailDialog.open, detailDialog.image?.uniqueId]);

  const handleDelete = useCallback((image) => {
    setDeleteDialog({ open: true, image });
  }, []);

  const handleDownload = useCallback(async (image) => {
    try {
      await imageService.downloadImage(image.uniqueId, image.originalName);
      setSnackbar({ open: true, message: 'Download started', severity: 'success' });
    } catch (error) {
      setSnackbar({ open: true, message: 'Download failed: ' + error.message, severity: 'error' });
    }
  }, []);

  // Save edited image
  const saveEdit = async () => {
    try {
      const { image } = editDialog;
      await imageService.updateImage(image.uniqueId, editFormData);
      
      showSnackbar('Image updated successfully', 'success');
      setEditDialog({ open: false, image: null });
      setEditFormData({ name: '', description: '', tags: '' });
      
      // Update the image in local state instead of reloading all images
      setImages(prevImages => 
        prevImages.map(img => 
          img.uniqueId === image.uniqueId 
            ? { ...img, ...editFormData }
            : img
        )
      );
      
    } catch (error) {
      showSnackbar('Update failed: ' + error.message, 'error');
    }
  };

  // Confirm delete
  const confirmDelete = async () => {
    setDeleting(true);
    try {
      const { image } = deleteDialog;
      await imageService.deleteImage(image.uniqueId);
      
      // Remove the deleted image from local state instead of reloading all images
      setImages(prevImages => prevImages.filter(img => img.uniqueId !== image.uniqueId));
      
      // Update total count
      setTotalImages(prevTotal => prevTotal - 1);
      
      showSnackbar('Image deleted successfully', 'success');
      setDeleteDialog({ open: false, image: null });
      
      // Only refresh stats (lightweight operation) - no need to reload all images
      await loadStats();
      
      // Trigger session dialog refresh to update image counts
      setSessionRefreshTrigger(prev => prev + 1);
      
    } catch (error) {
      showSnackbar('Delete failed: ' + error.message, 'error');
    } finally {
      setDeleting(false);
    }
  };

  // Bulk delete selected images
  const handleBulkDelete = () => {
    if (selectedImageIds.length === 0) {
      showSnackbar('No images selected for deletion', 'warning');
      return;
    }
    
    // Set up a special bulk delete dialog state
    setDeleteDialog({ 
      open: true, 
      image: null, 
      bulkDelete: true, 
      selectedCount: selectedImageIds.length 
    });
  };

  // Confirm bulk delete
  const confirmBulkDelete = async () => {
    setDeleting(true);
    try {
      const imageIds = [...selectedImageIds]; // Copy the array
      const result = await imageService.deleteImages(imageIds);
      
      // Remove the deleted images from local state
      setImages(prevImages => 
        prevImages.filter(img => !imageIds.includes(img.uniqueId))
      );
      
      // Update total count
      setTotalImages(prevTotal => prevTotal - result.results.deletedCount);
      
      // Clear selection
      setSelectedImageIds([]);
      
      // Show success message with details
      let message = `Successfully deleted ${result.results.deletedCount} image(s)`;
      if (result.results.blobDeleteErrors.length > 0) {
        message += ` (${result.results.blobDeleteErrors.length} storage errors)`;
      }
      
      showSnackbar(message, 'success');
      setDeleteDialog({ open: false, image: null, bulkDelete: false, selectedCount: 0 });
      
      // Only refresh stats (lightweight operation)
      await loadStats();
      
      // Trigger session dialog refresh to update image counts
      setSessionRefreshTrigger(prev => prev + 1);
      
    } catch (error) {
      showSnackbar('Bulk delete failed: ' + error.message, 'error');
    } finally {
      setDeleting(false);
    }
  };

  // Image analysis functions
  const checkAnalysisServiceStatus = useCallback(async () => {
    try {
      const status = await imageService.getAnalysisServiceStatus();
      setAnalysisServiceStatus(status);
    } catch (error) {
      logger.warn('Failed to check analysis service status', { error: error.message });
      setAnalysisServiceStatus({ available: false, error: error.message });
    }
  }, []);

  // Check analysis service status on component mount
  useEffect(() => {
    checkAnalysisServiceStatus();
  }, [checkAnalysisServiceStatus]);

  const handleImageSelection = useCallback((imageId, selected) => {
    setSelectedImageIds(prev => {
      if (selected) {
        return [...prev, imageId];
      } else {
        return prev.filter(id => id !== imageId);
      }
    });
  }, []);

  const handleSelectAllImages = useCallback((selected) => {
    if (selected) {
      setSelectedImageIds(images.map(img => img.uniqueId));
    } else {
      setSelectedImageIds([]);
    }
  }, [images]);

  const startImageAnalysis = () => {
    if (selectedImageIds.length === 0) {
      showSnackbar('Please select at least one image to analyze', 'warning');
      return;
    }

    if (!analysisServiceStatus?.anyServiceAvailable) {
      showSnackbar('Image analysis service is not available', 'error');
      return;
    }

    setAnalysisDialog({ open: true, loading: false, results: null });
  };

  const performImageAnalysis = async () => {
    try {
      setAnalysisDialog(prev => ({ ...prev, loading: true }));

      logger.info('Starting image analysis', {
        imageCount: selectedImageIds.length,
        hasCustomPrompt: !!analysisPrompt,
        provider: analysisProvider
      });

      const result = await imageService.analyzeImages(
        selectedImageIds,
        analysisPrompt || null,
        analysisProvider
      );

      logger.info('Image analysis completed', result);

      setAnalysisDialog(prev => ({
        ...prev,
        loading: false,
        results: result
      }));

      if (result.failed > 0) {
        showSnackbar(
          `Analysis completed: ${result.analyzed} successful, ${result.failed} failed`,
          'warning'
        );
      } else {
        showSnackbar(`Successfully analyzed ${result.analyzed} images`, 'success');
      }

    } catch (error) {
      logger.error('Image analysis failed', { error: error.message });
      setAnalysisDialog(prev => ({ ...prev, loading: false }));
      showSnackbar('Analysis failed: ' + error.message, 'error');
    }
  };

  const closeAnalysisDialog = () => {
    setAnalysisDialog({ open: false, loading: false, results: null });
    setAnalysisPrompt('');
    setSelectedImageIds([]);
  };

  // Face recognition handlers
  const startFaceRecognition = () => {
    if (selectedImageIds.length === 0) {
      showSnackbar('Please select at least one image for face recognition', 'warning');
      return;
    }

    if (!analysisServiceStatus?.face?.available) {
      showSnackbar('Face recognition service is not available', 'error');
      return;
    }

    setFaceRecognitionDialog({ open: true, loading: false, results: null });
  };

  const performFaceRecognition = async () => {
    try {
      setFaceRecognitionDialog(prev => ({ ...prev, loading: true }));

      logger.info('Starting face recognition', {
        imageCount: selectedImageIds.length,
        includeAttributes
      });

      const result = await imageService.recognizeFaces(selectedImageIds, includeAttributes);

      logger.info('Face recognition completed', result);

      setFaceRecognitionDialog(prev => ({
        ...prev,
        loading: false,
        results: result
      }));

      if (result.totalFaces > 0) {
        showSnackbar(
          `Face recognition completed: Found ${result.totalFaces} faces in ${result.processedImages} images`,
          'success'
        );
      } else {
        showSnackbar('No faces were detected in the selected images', 'info');
      }

    } catch (error) {
      logger.error('Face recognition failed', { error: error.message });
      setFaceRecognitionDialog(prev => ({ ...prev, loading: false }));
      showSnackbar('Face recognition failed: ' + error.message, 'error');
    }
  };

  const closeFaceRecognitionDialog = () => {
    setFaceRecognitionDialog({ open: false, loading: false, results: null });
    setIncludeAttributes(false);
  };

  // AWS Face Recognition handlers
  const startAwsFaceRecognition = async () => {
    if (!currentSession) {
      showSnackbar('Please select a session for AWS face search', 'warning');
      return;
    }

    console.log('🚀 Starting AWS Face Recognition, current hasInProgressOperation:', hasInProgressOperation);
    
    // Always check status first to ensure we have the latest state
    await checkAwsOperationStatus();
    
    // After checking, if there's an operation in progress, open dialog to show it
    // Need a small delay to let state update
    setTimeout(() => {
      if (hasInProgressOperation) {
        console.log('✅ Opening dialog with in-progress operation');
        setAwsFaceDialog(prev => ({ ...prev, open: true }));
      } else {
        console.log('➡️ Opening dialog for new operation');
        setAwsFaceDialog({ open: true, loading: false, results: null, showProgress: false });
      }
    }, 100);
  };

  const performAwsFaceRecognition = async () => {
    if (!currentSession) {
      showSnackbar('Please select a session for AWS face search', 'warning');
      return;
    }

    // Allow processing all images if none selected
    const imagesToProcess = selectedImageIds.length > 0 ? selectedImageIds.length : totalImages;
    const processingAllImages = selectedImageIds.length === 0;

    try {
      setHasInProgressOperation(true);
      setAwsFaceDialog(prev => ({ 
        ...prev, 
        loading: true,
        showProgress: true,
        progressData: {
          percentage: 0,
          current: 0,
          total: imagesToProcess,
          status: 'Initializing face recognition...',
          isCompleted: false,
          hasError: false,
          results: null
        }
      }));

      logger.info('Starting AWS face recognition with reference persons', {
        sessionId: currentSession.uniqueId,
        processingAllImages,
        imageCount: imagesToProcess,
        selectedImageIds: selectedImageIds.length > 0 ? selectedImageIds : 'all'
      });

      // Define progress callback
      const progressCallback = (data) => {
        console.log('🔄 Progress callback received:', data);
        setAwsFaceDialog(prev => ({
          ...prev,
          progressData: data
        }));
        
        // Handle completion
        if (data.isCompleted && !data.hasError) {
          const wasCancelled = data.cancelled || data.results?.cancelled || false;
          console.log(wasCancelled ? '🛑 Operation was cancelled:' : '✅ Progress completed:', data);
          setHasInProgressOperation(false);
          setAwsFaceDialog(prev => ({
            ...prev,
            loading: false,
            showProgress: false,
            results: wasCancelled ? null : data.results
          }));
          
          if (wasCancelled) {
            const processedImages = data.results?.processedImages ?? data.processedImages ?? 0;
            showSnackbar(
              `Face recognition cancelled after processing ${processedImages} image${processedImages !== 1 ? 's' : ''}.`,
              'warning'
            );
          } else {
            // Refresh images to show updated face detection data
            console.log('🔄 Reloading images after face recognition completion');
            loadImages();
            
            if (data.results?.matches && data.results.matches.length > 0) {
              // Calculate unique persons found
              const uniquePersons = [...new Set(data.results.matches.map(match => match.personName))];
              showSnackbar(
                `AWS Face search completed: Found ${uniquePersons.length} unique match${uniquePersons.length !== 1 ? 'es' : ''} - ${uniquePersons.join(', ')}`,
                'success'
              );
            } else if (data.results?.referencePersons && data.results.referencePersons.length === 0) {
              showSnackbar('No reference persons found in this session. Please add reference persons first.', 'info');
            } else {
              showSnackbar('No matches found for the reference persons in this session', 'info');
            }
          }
        }
        
        // Handle errors
        if (data.hasError) {
          console.log('❌ Progress error:', data);
          setHasInProgressOperation(false);
          setAwsFaceDialog(prev => ({
            ...prev,
            loading: false,
            showProgress: false
          }));
          showSnackbar('AWS Face search failed: ' + (data.error || 'Unknown error'), 'error');
        }
      };

      // Call the AWS recognition service using existing reference persons with progress monitoring
      const result = await imageService.findPersonMatchesWithAws(
        currentSession.uniqueId,
        progressCallback,
        selectedImageIds.length > 0 ? selectedImageIds : null, // Pass null for all images
        awsFaceOptions    // Pass face search options
      );

      logger.info('AWS face recognition completed', result);

      // If no progress was reported (fallback) - check if progressData exists and operation wasn't completed via SSE
      const currentState = awsFaceDialog.progressData || {};
      if (!currentState.isCompleted && !currentState.hasError) {
        setHasInProgressOperation(false);
        setAwsFaceDialog(prev => ({
          ...prev,
          loading: false,
          showProgress: false,
          results: result
        }));

        // Refresh images to show updated face detection data
        console.log('🔄 Reloading images after face recognition completion (fallback)');
        loadImages();

        if (result.matches && result.matches.length > 0) {
          // Calculate unique persons found
          const uniquePersons = [...new Set(result.matches.map(match => match.personName))];
          showSnackbar(
            `AWS Face search completed: Found ${uniquePersons.length} unique match${uniquePersons.length !== 1 ? 'es' : ''} - ${uniquePersons.join(', ')}`,
            'success'
          );
        } else if (result.referencePersons && result.referencePersons.length === 0) {
          showSnackbar('No reference persons found in this session. Please add reference persons first.', 'info');
        } else {
          showSnackbar('No matches found for the reference persons in this session', 'info');
        }
      }

    } catch (error) {
      logger.error('AWS Face recognition failed', { error: error.message });
      setHasInProgressOperation(false);
      setAwsFaceDialog(prev => ({ 
        ...prev, 
        loading: false,
        showProgress: false
      }));
      
      // Handle 409 conflict (operation already in progress)
      if (error.response?.status === 409) {
        setStuckFaceOperation(true);
      } else {
        showSnackbar('AWS Face search failed: ' + (error.response?.data?.error || error.message), 'error');
      }
    }
  };

  const handleForceResetAndRetryFace = async () => {
    try {
      await imageService.forceResetAwsFaceRecognition(currentSession.uniqueId);
      setStuckFaceOperation(false);
      performAwsFaceRecognition();
    } catch (err) {
      showSnackbar('Failed to clear stuck operation: ' + err.message, 'error');
    }
  };

  const cancelAwsFaceRecognition = async () => {
    if (!currentSession) return;

    try {
      logger.info('Cancelling AWS Face Recognition operation', { sessionId: currentSession.uniqueId });
      
      setAwsFaceDialog(prev => ({
        ...prev,
        loading: false,
        progressData: {
          ...prev.progressData,
          statusMessage: 'Cancelling...'
        }
      }));

      await imageService.cancelAwsFaceRecognition(currentSession.uniqueId);
      
      setHasInProgressOperation(false);
      
      showSnackbar('AWS Face Recognition operation is being cancelled...', 'info');
      
      // The operation will complete via WebSocket updates
    } catch (error) {
      logger.error('Failed to cancel AWS Face Recognition', { error: error.message });
      showSnackbar('Failed to cancel operation: ' + error.message, 'error');
    }
  };

  const closeAwsFaceDialog = () => {
    setAwsFaceDialog({ 
      open: false, 
      loading: false, 
      results: null,
      showProgress: false,
      progressData: {
        percentage: 0,
        current: 0,
        total: 0,
        status: 'Initializing...',
        isCompleted: false,
        hasError: false,
        results: null
      }
    });
    
    // Refresh the photo display to show updated face recognition data
    setSessionRefreshTrigger(prev => prev + 1);
    
    // Also explicitly reload images to get the updated face detection data
    console.log('🔄 Reloading images after face recognition dialog close');
    loadImages();
  };

  // Toggle expanded state for face matches in table
  const toggleFaceMatchesExpanded = useCallback((imageId) => {
    setExpandedFaceMatches(prev => ({
      ...prev,
      [imageId]: !prev[imageId]
    }));
  }, []);

  // Utility functions
  const showSnackbar = useCallback((message, severity = 'info') => {
    setSnackbar({ open: true, message, severity });
  }, []);

  const closeSnackbar = () => {
    setSnackbar({ open: false, message: '', severity: 'info' });
  };

  // Update a single image in the current state (instead of refreshing all images)
  const updateSingleImage = useCallback(async (imageId) => {
    if (!currentSession || !imageId) {
      return;
    }

    try {
      // Fetch the updated image data from the server
      const updatedImage = await imageService.getImage(imageId);
      
      // Update the images state with the new data
      setImages(prevImages => 
        prevImages.map(image => 
          image.uniqueId === imageId ? { ...image, ...updatedImage } : image
        )
      );
      
      logger.info('Successfully updated single image in table', { imageId });
    } catch (error) {
      logger.error('Failed to update single image', { imageId, error: error.message });
      // Fallback to full refresh if single update fails
      loadImages();
    }
  }, [currentSession, loadImages]);

  const handleSearch = () => {
    setPage(0); // Reset to first page when searching
    loadImages();
  };

  const clearFilters = () => {
    setSearchQuery('');
    setFilters({
      hasGPS: '',
      dateFrom: '',
      dateTo: '',
      tags: ''
    });
    setPage(0);
  };

  // Render EXIF information
  const renderExifInfo = (exifData) => {
    if (!exifData || Object.keys(exifData).length === 0) {
      return <Typography variant="body2">No EXIF data available</Typography>;
    }

    return (
      <Box>
        {/* Camera Information */}
        {(exifData.make || exifData.model) && (
          <Box mb={2}>
            <Typography variant="subtitle2" gutterBottom>
              <Camera sx={{ mr: 1, verticalAlign: 'middle' }} />
              Camera Information
            </Typography>
            {exifData.make && (
              <Typography variant="body2">Make: {exifData.make}</Typography>
            )}
            {exifData.model && (
              <Typography variant="body2">Model: {exifData.model}</Typography>
            )}
            {exifData.software && (
              <Typography variant="body2">Software: {exifData.software}</Typography>
            )}
          </Box>
        )}

        {/* Photo Settings */}
        {(exifData.exposureTime || exifData.fNumber || exifData.iso || exifData.focalLength) && (
          <Box mb={2}>
            <Typography variant="subtitle2" gutterBottom>
              Photo Settings
            </Typography>
            {exifData.exposureTime && (
              <Typography variant="body2">Exposure: {exifData.exposureTime}</Typography>
            )}
            {exifData.fNumber && (
              <Typography variant="body2">Aperture: f/{exifData.fNumber}</Typography>
            )}
            {exifData.iso && (
              <Typography variant="body2">ISO: {exifData.iso}</Typography>
            )}
            {exifData.focalLength && (
              <Typography variant="body2">Focal Length: {exifData.focalLength}mm</Typography>
            )}
            {exifData.flash && (
              <Typography variant="body2">Flash: {exifData.flash}</Typography>
            )}
          </Box>
        )}

        {/* GPS Information */}
        {exifData.gps && (exifData.gps.latitude || exifData.gps.longitude) && (
          <Box mb={2}>
            <Typography variant="subtitle2" gutterBottom>
              <LocationOn sx={{ mr: 1, verticalAlign: 'middle' }} />
              GPS Information
            </Typography>
            {exifData.gps.latitude && exifData.gps.longitude && (
              <>
                <Typography variant="body2">
                  Coordinates: {exifData.gps.latitude.toFixed(6)}, {exifData.gps.longitude.toFixed(6)}
                </Typography>
                <Button
                  size="small"
                  startIcon={<Map />}
                  onClick={() => {
                    const url = `https://www.google.com/maps?q=${exifData.gps.latitude},${exifData.gps.longitude}`;
                    window.open(url, '_blank');
                  }}
                  sx={{ mt: 1 }}
                >
                  View on Map
                </Button>
              </>
            )}
            {exifData.gps.altitude && (
              <Typography variant="body2">Altitude: {exifData.gps.altitude}m</Typography>
            )}
            {exifData.gps.timestamp && (
              <Typography variant="body2">
                GPS Time: {formatDate(exifData.gps.timestamp)}
              </Typography>
            )}
          </Box>
        )}

        {/* Date Information */}
        {(exifData.dateTime || exifData.dateTimeOriginal) && (
          <Box mb={2}>
            <Typography variant="subtitle2" gutterBottom>
              <CalendarToday sx={{ mr: 1, verticalAlign: 'middle' }} />
              Date Information
            </Typography>
            {exifData.dateTimeOriginal && (
              <Typography variant="body2">
                Original: {formatDate(exifData.dateTimeOriginal)}
              </Typography>
            )}
            {exifData.dateTime && (
              <Typography variant="body2">
                Modified: {formatDate(exifData.dateTime)}
              </Typography>
            )}
          </Box>
        )}
      </Box>
    );
  };

  // Check user permissions - temporarily allow all for testing
  const canUpload = true; // user && ['admin', 'editor'].includes(user.permission);
  const canEdit = true; // user && ['admin', 'editor'].includes(user.permission);
  const canDelete = true; // user && ['admin', 'editor'].includes(user.permission);

  // Memoized AI Analysis toolbar to prevent re-renders during pagination
  const aiAnalysisToolbar = useMemo(() => {
    if (!analysisServiceStatus?.anyServiceAvailable) return null;
    
    return (
      <Box mb={2} p={2} bgcolor="grey.50" borderRadius={1}>
        {/* In-Progress Operation Notification */}
        {hasInProgressOperation && (
          <Alert 
            severity="info" 
            sx={{ mb: 2 }}
            action={
              <Button 
                color="inherit" 
                size="small" 
                onClick={viewInProgressOperation}
              >
                View Progress
              </Button>
            }
          >
            <Typography variant="body2">
              <strong>AWS Face Recognition in progress</strong>
              {awsFaceDialog.progressData?.percentage > 0 && (
                <> — {Math.round(awsFaceDialog.progressData.percentage)}% complete</>
              )}
            </Typography>
          </Alert>
        )}
        
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
          <Typography variant="subtitle2">
            AI Image Analysis {selectedImageIds.length > 0 && `(${selectedImageIds.length} selected)`}
          </Typography>
          <Box>
            <Button
              size="small"
              onClick={() => handleSelectAllImages(selectedImageIds.length !== images.length)}
              startIcon={selectedImageIds.length === images.length ? <CheckBox /> : <CheckBoxOutlineBlank />}
              sx={{ mr: 1 }}
            >
              {selectedImageIds.length === images.length ? 'Deselect All' : 'Select All'}
            </Button>
            <Button
              variant="contained"
              size="small"
              onClick={startImageAnalysis}
              disabled={selectedImageIds.length === 0}
              startIcon={<Psychology />}
              sx={{ mr: 1 }}
            >
              Analyze Selected
            </Button>
            <Button
              variant="contained"
              color="secondary"
              size="small"
              onClick={startFaceRecognition}
              disabled={selectedImageIds.length === 0 || !analysisServiceStatus?.face?.available}
              startIcon={<Face />}
              sx={{ mr: 1 }}
            >
              Recognize Faces
            </Button>
            <Button
              variant="contained"
              color="warning"
              size="small"
              onClick={startAwsFaceRecognition}
              disabled={!currentSession || totalImages === 0}
              startIcon={<CloudOutlined />}
              sx={{ mr: 1 }}
            >
              AWS Face Search {selectedImageIds.length > 0 ? `(${selectedImageIds.length} selected)` : '(All Images)'}
            </Button>
            {canDelete && (
              <Button
                variant="contained"
                color="error"
                size="small"
                onClick={handleBulkDelete}
                disabled={selectedImageIds.length === 0}
                startIcon={<Delete />}
              >
                Delete Selected
              </Button>
            )}
          </Box>
        </Box>
        {(!analysisServiceStatus?.openai?.configured && !analysisServiceStatus?.azure?.configured) && (
          <Alert severity="warning" size="small">
            No vision API keys configured. Analysis features are limited.
          </Alert>
        )}
      </Box>
    );
  }, [analysisServiceStatus, selectedImageIds.length, images.length, canDelete, currentSession, handleBulkDelete, handleSelectAllImages, startAwsFaceRecognition, startFaceRecognition, startImageAnalysis]);

  // Memoized Image Similarity section to prevent re-renders during pagination
  const imageSimilaritySection = useMemo(() => {
    if (!currentSession?.uniqueId) return null;
    
    return (
      <Box mb={2} p={2} bgcolor="secondary.50" borderRadius={1} border={1} borderColor="secondary.200">
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box>
            <Typography variant="subtitle2" color="secondary.main">
              🔍 AI-Powered Image Similarity Analysis
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
              Find visually similar images and automatically cluster related photos using vector embeddings
            </Typography>
          </Box>
          <Button
            variant="contained"
            color="secondary"
            size="small"
            onClick={() => setSimilarityDialog({ open: true })}
            startIcon={<Psychology />}
          >
            Image Similarity
          </Button>
        </Box>
      </Box>
    );
  }, [currentSession?.uniqueId]);

  // Memoized Enhanced Pagination Controls - used at top and bottom of table
  const paginationControls = useMemo(() => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2, borderTop: 1, borderColor: 'divider' }}>
      {/* Page size selector */}
      <FormControl size="small" sx={{ minWidth: 120 }}>
        <InputLabel>Images per page</InputLabel>
        <Select
          value={rowsPerPage}
          label="Images per page"
          onChange={(event) => {
            setRowsPerPage(parseInt(event.target.value, 10));
            setPage(0);
          }}
        >
          <MenuItem value={10}>10</MenuItem>
          <MenuItem value={25}>25</MenuItem>
          <MenuItem value={50}>50</MenuItem>
          <MenuItem value={100}>100</MenuItem>
          <MenuItem value={200}>200</MenuItem>
        </Select>
      </FormControl>

      {/* Page jump selector */}
      <FormControl size="small" sx={{ minWidth: 120 }}>
        <InputLabel>Jump to page</InputLabel>
        <Select
          value={page}
          label="Jump to page"
          onChange={(event) => setPage(event.target.value)}
        >
          {Array.from({ length: Math.ceil(totalImages / rowsPerPage) }, (_, i) => (
            <MenuItem key={i} value={i}>
              Page {i + 1}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* Standard pagination controls */}
      <TablePagination
        component="div"
        count={totalImages}
        page={page}
        onPageChange={(event, newPage) => setPage(newPage)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(event) => {
          setRowsPerPage(parseInt(event.target.value, 10));
          setPage(0);
        }}
        rowsPerPageOptions={[10, 25, 50, 100, 200]}
        sx={{ flex: 1 }}
      />
    </Box>
  ), [page, rowsPerPage, totalImages]);

  return (
    <Box sx={{ p: 3, maxWidth: '1400px', mx: 'auto' }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" gutterBottom>
            <ImageIcon sx={{ mr: 2, verticalAlign: 'middle' }} />
            Image Upload & Management 1.0
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            Upload multiple images and manage your photo collection with EXIF and GPS data
          </Typography>
        </Box>
        
        {stats && (
          <Card sx={{ minWidth: 200 }}>
            <CardContent sx={{ pb: 2 }}>
              <Typography variant="h6">{stats.totalImages}</Typography>
              <Typography variant="body2" color="text.secondary">
                Total Images
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {stats.imagesWithGPS} with GPS data
              </Typography>
            </CardContent>
          </Card>
        )}
      </Box>

      {/* Session Selection */}
      <Card sx={{ mb: 3, border: currentSession ? '2px solid' : '1px solid', borderColor: currentSession ? 'success.main' : 'grey.300' }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box display="flex" alignItems="center" gap={2}>
              <Folder color={currentSession ? 'success' : 'action'} />
              <Box>
                {currentSession ? (
                  <>
                    <Typography variant="h6" color="success.main">
                      Active Session: {currentSession.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {currentSession.description || 'No description'}
                    </Typography>
                    <Box display="flex" alignItems="center" gap={2} mt={1}>
                      <Chip
                        label={`${currentSession.formattedStats?.totalImages || 0} images`}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                      <Chip
                        label={currentSession.status}
                        size="small"
                        color={currentSession.status === 'active' ? 'success' : 'default'}
                      />
                      <Typography variant="caption" color="text.secondary">
                        Created {formatDate(currentSession.createdAt)}
                      </Typography>
                    </Box>
                    {/* Session Event Type selection moved to dedicated section below */}
                  </>
                ) : (
                  <>
                    <Typography variant="h6" color="warning.main">
                      No Session Selected
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Please select or create a session to upload images
                    </Typography>
                  </>
                )}
              </Box>
            </Box>
            <Box>
              <Button
                variant={currentSession ? "outlined" : "contained"}
                onClick={handleChangeSession}
                startIcon={<Folder />}
                color={currentSession ? "primary" : "warning"}
              >
                {currentSession ? "Change Session" : "Select Session"}
              </Button>
              {currentSession && (
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<DeleteForever />}
                  sx={{ ml: 2 }}
                  onClick={handleDeleteAllImagesInSession}
                  disabled={deletingAllImages}
                >
                  {deletingAllImages ? 'Deleting Images...' : 'Delete All Images'}
                </Button>
              )}
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Session Event Types Section */}
      {currentSession && (
        <Card sx={{ mb: 3, border: '1px solid', borderColor: 'grey.300' }}>
          <CardContent>
            <SessionEventTypeManager
              currentSession={currentSession}
              refreshCurrentSession={refreshCurrentSession}
              showSnackbar={showSnackbar}
            />
          </CardContent>
        </Card>
      )}

      {/* Face Recognition Card */}
      {currentSession && (
        <FaceRecognitionCard
          sessionId={currentSession.uniqueId}
          refreshTrigger={sessionRefreshTrigger}
        />
      )}

      {/* Upload Section */}
      {canUpload && (
        <Accordion defaultExpanded sx={{ mb: 3 }}>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Typography variant="h6">
              <CloudUpload sx={{ mr: 1, verticalAlign: 'middle' }} />
              Upload Images
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={3}>
              {/* Dropzone */}
              <Grid item xs={12} md={6}>
                <Box
                  {...getRootProps()}
                  sx={{
                    border: '2px dashed',
                    borderColor: isDragActive ? 'primary.main' : 'grey.300',
                    borderRadius: 2,
                    p: 4,
                    textAlign: 'center',
                    cursor: 'pointer',
                    bgcolor: isDragActive ? 'action.hover' : 'background.paper',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      borderColor: 'primary.main',
                      bgcolor: 'action.hover'
                    }
                  }}
                >
                  <input
                    {...getInputProps({
                      onChange: (event) => {
                        const files = Array.from(event.target.files || []);
                        logger.info('Native file input changed', {
                          selectedCount: files.length,
                          names: files.map(file => file.name)
                        });
                      }
                    })}
                  />
                  <PhotoLibrary sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
                  <Typography variant="h6" gutterBottom>
                    {isDragActive
                      ? 'Drop the images here...'
                      : 'Drag & drop images here, or click to select'
                    }
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Supports JPEG, PNG, GIF, WebP, BMP, TIFF, HEIC (max 50MB each)
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontStyle: 'italic' }}>
                    HEIC files will be automatically converted to JPEG for compatibility
                  </Typography>
                </Box>
              </Grid>

              {/* Alternative Upload Methods */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom sx={{ mt: 2, mb: 1 }}>
                  Alternative Upload Methods
                </Typography>
                <Box display="flex" gap={1} flexWrap="wrap">
                  <Button
                    variant="outlined"
                    startIcon={<PhotoLibrary />}
                    onClick={() => setGooglePhotosDialog({ open: true })}
                    disabled={uploading}
                  >
                    Import from Google Photos
                  </Button>
                  {/* Add more alternative upload methods here in the future */}
                </Box>
              </Grid>

              {/* File List */}
              <Grid item xs={12} md={6}>
                {previewFiles.length > 0 && (
                  <Box>
                    <Typography variant="h6" gutterBottom>
                      Selected Files ({previewFiles.length})
                    </Typography>
                    <Box sx={{ maxHeight: 300, overflowY: 'auto' }}>
                      {previewFiles.map((preview, index) => (
                        <Card key={index} sx={{ mb: 1, display: 'flex', alignItems: 'center' }}>
                          {preview.isHeic && !preview.preview ? (
                            // Show HEIC placeholder icon when preview generation failed
                            <Box sx={{
                              width: 60,
                              height: 60,
                              borderRadius: 1,
                              m: 1,
                              bgcolor: 'grey.200',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              border: '1px solid',
                              borderColor: 'grey.300'
                            }}>
                              <PhotoIcon sx={{ color: 'grey.500' }} />
                            </Box>
                          ) : preview.preview ? (
                            // Show preview for all formats (including converted HEIC)
                            <Box
                              component="img"
                              src={preview.preview}
                              sx={{
                                width: 60,
                                height: 60,
                                objectFit: 'cover',
                                borderRadius: 1,
                                m: 1
                              }}
                            />
                          ) : (
                            // Fallback placeholder for any other case
                            <Box sx={{
                              width: 60,
                              height: 60,
                              borderRadius: 1,
                              m: 1,
                              bgcolor: 'grey.200',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              border: '1px solid',
                              borderColor: 'grey.300'
                            }}>
                              <PhotoIcon sx={{ color: 'grey.500' }} />
                            </Box>
                          )}
                          <Box sx={{ flex: 1, p: 1 }}>
                            <TextField
                              fullWidth
                              size="small"
                              label="Name"
                              value={preview.name}
                              onChange={(e) => updateFileMetadata(index, 'name', e.target.value)}
                              sx={{ mb: 1 }}
                            />
                            <TextField
                              fullWidth
                              size="small"
                              label="Description"
                              value={preview.description}
                              onChange={(e) => updateFileMetadata(index, 'description', e.target.value)}
                              sx={{ mb: 1 }}
                            />
                            <TextField
                              fullWidth
                              size="small"
                              label="Tags (comma-separated)"
                              value={preview.tags}
                              onChange={(e) => updateFileMetadata(index, 'tags', e.target.value)}
                            />
                          </Box>
                          <IconButton
                            onClick={() => removeFile(index)}
                            color="error"
                            sx={{ m: 1 }}
                          >
                            <Delete />
                          </IconButton>
                        </Card>
                      ))}
                    </Box>
                  </Box>
                )}
              </Grid>
            </Grid>

            {/* Upload Controls */}
            {previewFiles.length > 0 && (
              <Box mt={3}>
                          {/* Session-level event type is edited in the Session card above */}
                {uploading && (
                  <Box mb={2}>
                    <Typography variant="body2" gutterBottom>
                      Uploading... {uploadProgress}%
                    </Typography>
                    <LinearProgress variant="determinate" value={uploadProgress} />
                  </Box>
                )}
                <Box display="flex" gap={2}>
                  <Button
                    variant="contained"
                    startIcon={<CloudUpload />}
                    onClick={handleUpload}
                    disabled={uploading}
                  >
                    {uploading ? 'Uploading...' : `Upload ${previewFiles.length} Images`}
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={() => {
                      previewFiles.forEach(preview => {
                        if (preview.preview) {
                          URL.revokeObjectURL(preview.preview);
                        }
                      });
                      setSelectedFiles([]);
                      setPreviewFiles([]);
                    }}
                    disabled={uploading}
                  >
                    Clear All
                  </Button>
                </Box>
              </Box>
            )}
          </AccordionDetails>
        </Accordion>
      )}

      {/* Search and Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                placeholder="Search images..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  )
                }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <Button
                variant="contained"
                onClick={handleSearch}
                fullWidth
              >
                Search
              </Button>
            </Grid>
            <Grid item xs={12} md={2}>
              <Button
                variant="outlined"
                startIcon={<FilterList />}
                onClick={() => setShowFilters(!showFilters)}
                fullWidth
              >
                Filters
              </Button>
            </Grid>
            <Grid item xs={12} md={2}>
              <Button
                variant="outlined"
                onClick={clearFilters}
                fullWidth
              >
                Clear
              </Button>
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Sort By</InputLabel>
                <Select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  label="Sort By"
                >
                  <MenuItem value="createdAt">Date</MenuItem>
                  <MenuItem value="name">Name</MenuItem>
                  <MenuItem value="fileSize">Size</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          {/* Advanced Filters */}
          {showFilters && (
            <Box mt={3} pt={3} borderTop="1px solid" borderColor="divider">
              <Grid container spacing={2}>
                <Grid item xs={12} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>GPS Data</InputLabel>
                    <Select
                      value={filters.hasGPS}
                      onChange={(e) => setFilters({ ...filters, hasGPS: e.target.value })}
                      label="GPS Data"
                    >
                      <MenuItem value="">All</MenuItem>
                      <MenuItem value="true">With GPS</MenuItem>
                      <MenuItem value="false">Without GPS</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    size="small"
                    type="date"
                    label="From Date"
                    value={filters.dateFrom}
                    onChange={(e) => setFilters({ ...filters, dateFrom: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    size="small"
                    type="date"
                    label="To Date"
                    value={filters.dateTo}
                    onChange={(e) => setFilters({ ...filters, dateTo: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Filter by tags..."
                    value={filters.tags}
                    onChange={(e) => setFilters({ ...filters, tags: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Embedding Status</InputLabel>
                    <Select
                      value={filters.embeddingStatus}
                      onChange={(e) => setFilters({ ...filters, embeddingStatus: e.target.value })}
                      label="Embedding Status"
                    >
                      <MenuItem value="all">All Images</MenuItem>
                      <MenuItem value="embedded">With Embedding</MenuItem>
                      <MenuItem value="not-embedded">Without Embedding</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Images Table */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Images ({totalImages})
          </Typography>
          
          {/* Always show structure, only replace table content with spinner */}
          <>
            {/* Analysis toolbar - Memoized to prevent re-renders during pagination */}
            {aiAnalysisToolbar}

            {/* Image Similarity & Clustering - Memoized to prevent re-renders during pagination */}
            {imageSimilaritySection}

            {/* Enhanced Pagination Controls (Top) - Spinner waits for image data from API */}
            {paginationControls}

            {/* Memoized table to prevent re-renders when dialog state changes */}
            {useMemo(() => (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      {analysisServiceStatus?.anyServiceAvailable && (
                        <TableCell padding="checkbox">
                          <IconButton
                            onClick={() => handleSelectAllImages(selectedImageIds.length !== images.length)}
                            size="small"
                          >
                            {selectedImageIds.length === images.length ? <CheckBox /> : <CheckBoxOutlineBlank />}
                          </IconButton>
                        </TableCell>
                      )}
                      <TableCell>Preview</TableCell>
                      <TableCell>Name</TableCell>
                      <TableCell>Size</TableCell>
                      <TableCell>Dimensions</TableCell>
                      <TableCell>Date/GPS</TableCell>
                      <TableCell>Face Matches</TableCell>
                      <TableCell>Upload Date</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                <TableBody>
                  {(() => {
                    // Filter images based on embedding status
                    const filteredImages = images.filter(image => {
                      if (filters.embeddingStatus === 'embedded') {
                        return image.hasEmbedding === true;
                      } else if (filters.embeddingStatus === 'not-embedded') {
                        return image.hasEmbedding !== true;
                      }
                      return true;
                    });

                    // Render fixed number of rows based on rowsPerPage
                    return Array.from({ length: rowsPerPage }, (_, index) => {
                      const image = filteredImages[index];
                      
                      if (loading) {
                        // Show skeleton/loading state in each row
                        return (
                          <TableRow key={`loading-${index}`}>
                            {analysisServiceStatus?.anyServiceAvailable && (
                              <TableCell padding="checkbox">
                                <Skeleton variant="rectangular" width={24} height={24} />
                              </TableCell>
                            )}
                            <TableCell><Skeleton variant="rectangular" width={60} height={60} /></TableCell>
                            <TableCell><Skeleton variant="text" /></TableCell>
                            <TableCell><Skeleton variant="text" /></TableCell>
                            <TableCell><Skeleton variant="text" /></TableCell>
                            <TableCell><Skeleton variant="text" /></TableCell>
                            <TableCell><Skeleton variant="text" /></TableCell>
                            <TableCell><Skeleton variant="text" /></TableCell>
                            <TableCell><Skeleton variant="text" /></TableCell>
                          </TableRow>
                        );
                      }

                      if (!image) {
                        // Empty row for consistency (when fewer images than rowsPerPage)
                        return null;
                      }

                      // Render actual image data
                      return (
                      <TableRow key={image.uniqueId}>
                        {analysisServiceStatus?.anyServiceAvailable && (
                          <TableCell padding="checkbox">
                            <Checkbox
                              checked={selectedImageIds.includes(image.uniqueId)}
                              onChange={() => handleImageSelection(image.uniqueId, !selectedImageIds.includes(image.uniqueId))}
                            />
                          </TableCell>
                        )}
                        <TableCell>
                          <AuthenticatedImage
                            imageId={image.uniqueId}
                            type="thumbnail"
                            alt={image.name}
                            onClick={() => handleView(image)}
                            sx={{
                              width: 60,
                              height: 60,
                              objectFit: 'cover',
                              borderRadius: 1,
                              cursor: 'pointer'
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={1}>
                            <Typography variant="body2">{image.name}</Typography>
                            {image.isReferencePhoto && (
                              <Tooltip title={
                                `Reference photo for: ${image.referencePersons?.map(rp => rp.personName).join(', ')}`
                              }>
                                <PersonPin 
                                  color="primary" 
                                  fontSize="small"
                                  sx={{ ml: 0.5 }}
                                />
                              </Tooltip>
                            )}
                            {image.uploadedBy && (
                              <Tooltip title={
                                `Uploaded by: ${image.uploadedBy.username || image.uploadedBy.email || 'Unknown user'}`
                              }>
                                <AccountCircle 
                                  color="action" 
                                  fontSize="small"
                                  sx={{ ml: 0.5, opacity: 0.7 }}
                                />
                              </Tooltip>
                            )}
                            {image.hasEmbedding && (
                              <Tooltip title="Vector embedding generated">
                                <Psychology 
                                  color="success" 
                                  fontSize="small"
                                  sx={{ ml: 0.5 }}
                                />
                              </Tooltip>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {formatFileSize(image.fileSize)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {image.contentType}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {image.dimensionsString || 'Unknown'}
                        </TableCell>
                        <TableCell>
                          {image.exifData?.dateTimeOriginal ? (
                            <Tooltip title={`Full datetime: ${new Date(image.exifData.dateTimeOriginal).toLocaleString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false })}`}>
                              <Box>
                                <Typography variant="body2" color="primary" sx={{ fontSize: '0.75rem', lineHeight: 1.2 }}>
                                  {new Date(image.exifData.dateTimeOriginal).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                                  {new Date(image.exifData.dateTimeOriginal).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false })}
                                </Typography>
                              </Box>
                            </Tooltip>
                          ) : image.exifData?.gps?.latitude ? (
                            <Tooltip title={`${image.exifData.gps.latitude.toFixed(6)}, ${image.exifData.gps.longitude.toFixed(6)}`}>
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => {
                                  const url = `https://www.google.com/maps?q=${image.exifData.gps.latitude},${image.exifData.gps.longitude}`;
                                  window.open(url, '_blank');
                                }}
                              >
                                <LocationOn />
                              </IconButton>
                            </Tooltip>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              No date/GPS
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {image.faceRecognition?.matches && image.faceRecognition.matches.length > 0 ? (
                            <Box>
                              {(() => {
                                // Get unique person names from matches
                                // Filter out any undefined/null personNames
                                const uniquePersons = [...new Set(
                                  image.faceRecognition.matches
                                    .map(match => match.personName)
                                    .filter(name => name && name !== 'undefined')
                                )];
                                
                                // Warning if no valid person names found
                                if (uniquePersons.length === 0 && image.faceRecognition.matches.length > 0) {
                                  console.warn('⚠️ Image has matches but no valid person names:', image.name);
                                  console.warn('⚠️ This may indicate old data format. Re-run face recognition to fix.');
                                }
                                
                                const uniqueCount = uniquePersons.length;
                                
                                // If no valid data, show a message instead of rendering empty matches
                                if (uniqueCount === 0) {
                                  return (
                                    <Tooltip title="Data incomplete. Please re-run AWS Face Recognition to update.">
                                      <Typography variant="caption" color="warning.main" sx={{ fontStyle: 'italic' }}>
                                        {image.faceRecognition.matches.length} match{image.faceRecognition.matches.length !== 1 ? 'es' : ''} (incomplete data)
                                      </Typography>
                                    </Tooltip>
                                  );
                                }
                                
                                const isExpanded = expandedFaceMatches[image.uniqueId] || false;
                                const maxDisplayCount = 2;
                                const hasMorePersons = uniqueCount > maxDisplayCount;
                                const displayPersons = isExpanded ? uniquePersons : uniquePersons.slice(0, maxDisplayCount);
                                
                                // Find the main person (highest similarity) - only if there are multiple persons
                                let mainPerson = null;
                                let highestSimilarity = 0;
                                if (uniqueCount > 1) {
                                  image.faceRecognition.matches.forEach(match => {
                                    // Check different possible property names for similarity
                                    const sim = match.similarity || match.Similarity || match.confidence || match.Confidence || 0;
                                    if (sim > highestSimilarity) {
                                      highestSimilarity = sim;
                                      mainPerson = match.personName;
                                    }
                                  });
                                }
                                
                                return (
                                  <>
                                    <Box display="flex" alignItems="center" mb={1}>
                                      <Typography variant="body2" color="primary" sx={{ fontWeight: 'bold' }}>
                                        {uniqueCount} match{uniqueCount !== 1 ? 'es' : ''}
                                      </Typography>
                                      {hasMorePersons && (
                                        <IconButton
                                          size="small"
                                          onClick={() => toggleFaceMatchesExpanded(image.uniqueId)}
                                          sx={{ ml: 1, p: 0.5 }}
                                        >
                                          {isExpanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                                        </IconButton>
                                      )}
                                    </Box>
                                    {displayPersons.map((personName, index) => {
                                      // Find the best match for this person
                                      const personMatches = image.faceRecognition.matches.filter(match => match.personName === personName);
                                      
                                      // Try to find similarity from various possible property names
                                      let bestMatch = null;
                                      let bestSimilarity = 0;
                                      
                                      personMatches.forEach(match => {
                                        // Check different possible property names for similarity
                                        const sim = match.similarity || match.Similarity || match.confidence || match.Confidence || 0;
                                        
                                        if (sim > bestSimilarity) {
                                          bestSimilarity = sim;
                                          bestMatch = match;
                                        }
                                      });
                                      
                                      // If still 0, log for debugging
                                      if (bestSimilarity === 0 && personMatches.length > 0) {
                                        console.warn('⚠️ Zero similarity found for person:', personName);
                                        console.warn('⚠️ First match data:', JSON.stringify(personMatches[0], null, 2));
                                      }
                                      
                                      const isMainPerson = (uniqueCount > 1) && (personName === mainPerson);
                                      
                                      return (
                                        <Box key={index} display="flex" alignItems="center" gap={0.5}>
                                          <Typography variant="caption" color="text.secondary">
                                            {personName}{bestSimilarity > 0 ? `: ${bestSimilarity.toFixed(1)}%` : ' (no similarity data)'}
                                          </Typography>
                                          {isMainPerson && (
                                            <Chip 
                                              label="Main"
                                              size="small"
                                              color="primary"
                                              sx={{ 
                                                height: '16px', 
                                                fontSize: '0.6rem',
                                                '& .MuiChip-label': { px: 0.5 }
                                              }}
                                            />
                                          )}
                                        </Box>
                                      );
                                    })}
                                    {!isExpanded && hasMorePersons && (
                                      <Typography 
                                        variant="caption" 
                                        color="primary" 
                                        sx={{ cursor: 'pointer', textDecoration: 'underline' }}
                                        onClick={() => toggleFaceMatchesExpanded(image.uniqueId)}
                                      >
                                        +{uniqueCount - maxDisplayCount} more
                                      </Typography>
                                    )}
                                  </>
                                );
                              })()}
                            </Box>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              No matches
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {formatDate(image.createdAt)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box display="flex" gap={0.5}>
                            <Tooltip title="View Details">
                              <IconButton
                                size="small"
                                onClick={() => handleView(image)}
                              >
                                <Visibility />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Download">
                              <IconButton
                                size="small"
                                onClick={() => handleDownload(image)}
                              >
                                <Download />
                              </IconButton>
                            </Tooltip>
                            {canEdit && (
                              <Tooltip title="Edit">
                                <IconButton
                                  size="small"
                                  onClick={() => handleEdit(image)}
                                >
                                  <Edit />
                                </IconButton>
                              </Tooltip>
                            )}
                            <Tooltip title="Face Recognition">
                              <span>
                                <FaceRecognitionMenu 
                                  image={image} 
                                  sessionId={currentSession?.uniqueId} 
                                  onReferencePersonAdded={() => setSessionRefreshTrigger(prev => prev + 1)}
                                  onSuccess={() => updateSingleImage(image.uniqueId)}
                                />
                              </span>
                            </Tooltip>
                            {canDelete && (
                              <Tooltip title="Delete">
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => handleDelete(image)}
                                >
                                  <Delete />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                    });
                  })()}
                </TableBody>
              </Table>
            </TableContainer>
            ), [images, selectedImageIds, loading, filters, rowsPerPage, analysisServiceStatus, expandedFaceMatches, currentSession, canEdit, canDelete, handleSelectAllImages, handleImageSelection, handleView, handleDownload, handleEdit, handleDelete, toggleFaceMatchesExpanded, updateSingleImage])}

            {/* Enhanced Pagination Controls (Bottom) */}
            {paginationControls}
          </>
        </CardContent>
      </Card>

      {/* Detail Dialog */}
      <Dialog
        open={detailDialog.open}
        onClose={() => setDetailDialog({ open: false, image: null })}
        maxWidth="md"
        fullWidth
      >
        {detailDialog.image && (
          <>
            <DialogTitle>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="h6">{detailDialog.image.name}</Typography>
                <IconButton onClick={() => setDetailDialog({ open: false, image: null })}>
                  <Close />
                </IconButton>
              </Box>
            </DialogTitle>
            <DialogContent>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <AuthenticatedImage
                    imageId={detailDialog.image.uniqueId}
                    type="serve"
                    alt={detailDialog.image.name}
                    sx={{
                      width: '100%',
                      maxHeight: 400,
                      objectFit: 'contain',
                      borderRadius: 1
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom>
                    <Info sx={{ mr: 1, verticalAlign: 'middle' }} />
                    File Information
                  </Typography>
                  <Typography variant="body2" gutterBottom>
                    <strong>Original Name:</strong> {detailDialog.image.originalName}
                  </Typography>
                  <Typography variant="body2" gutterBottom>
                    <strong>Size:</strong> {formatFileSize(detailDialog.image.fileSize)}
                  </Typography>
                  <Typography variant="body2" gutterBottom>
                    <strong>Type:</strong> {detailDialog.image.contentType}
                  </Typography>
                  <Typography variant="body2" gutterBottom>
                    <strong>Dimensions:</strong> {detailDialog.image.dimensionsString || 'Unknown'}
                  </Typography>
                  <Typography variant="body2" gutterBottom>
                    <strong>Uploaded:</strong> {formatDate(detailDialog.image.createdAt)}
                  </Typography>
                  
                  {detailDialog.image.isReferencePhoto && (
                    <Typography variant="body2" gutterBottom>
                      <strong>Reference Photo:</strong> 
                      <Box component="span" sx={{ ml: 1 }}>
                        <PersonPin color="primary" fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                        Used for {detailDialog.image.referencePersons?.map(rp => rp.personName).join(', ')}
                      </Box>
                    </Typography>
                  )}
                  
                  {detailDialog.image.description && (
                    <Typography variant="body2" gutterBottom>
                      <strong>Description:</strong> {detailDialog.image.description}
                    </Typography>
                  )}
                  
                  {detailDialog.image.tags && detailDialog.image.tags.length > 0 && (
                    <Box mt={2}>
                      <Typography variant="body2" gutterBottom>
                        <strong>Tags:</strong>
                      </Typography>
                      <Box>
                        {detailDialog.image.tags.map((tag, index) => (
                          <Chip key={index} label={tag} size="small" sx={{ mr: 0.5, mb: 0.5 }} />
                        ))}
                      </Box>
                    </Box>
                  )}

                  <Box mt={3}>
                    <Typography variant="h6" gutterBottom>
                      EXIF Data
                    </Typography>
                    {renderExifInfo(detailDialog.image.exifData)}
                  </Box>

                  <Box mt={3}>
                    <Typography variant="h6" gutterBottom>
                      Embedding Debug Data
                    </Typography>

                    {embeddingDetailsLoading && (
                      <Box display="flex" alignItems="center" gap={1}>
                        <CircularProgress size={18} />
                        <Typography variant="body2" color="text.secondary">
                          Loading embedding details...
                        </Typography>
                      </Box>
                    )}

                    {!embeddingDetailsLoading && embeddingDetailsError && (
                      <Typography variant="body2" color="warning.main">
                        {embeddingDetailsError}
                      </Typography>
                    )}

                    {!embeddingDetailsLoading && !embeddingDetailsError && embeddingDetails && (
                      <Box>
                        <Paper elevation={1} sx={{ p: 1.5, mb: 1.5, bgcolor: 'background.default' }}>
                          <Grid container spacing={1.5}>
                            <Grid item xs={6}>
                              <Typography variant="caption" color="text.secondary">Image ID</Typography>
                              <Typography variant="body2">{embeddingDetails.imageId || 'N/A'}</Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography variant="caption" color="text.secondary">Session ID</Typography>
                              <Typography variant="body2">{embeddingDetails.sessionId || 'N/A'}</Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography variant="caption" color="text.secondary">Embedding Model</Typography>
                              <Typography variant="body2">{embeddingDetails.embeddingModel || 'N/A'}</Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography variant="caption" color="text.secondary">Embedding Dimensions</Typography>
                              <Typography variant="body2">{embeddingDetails.embeddingDimensions || embeddingDetails.embedding?.length || 'N/A'}</Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography variant="caption" color="text.secondary">Processing Time</Typography>
                              <Typography variant="body2">{embeddingDetails.processingTimeMs ? `${embeddingDetails.processingTimeMs} ms` : 'N/A'}</Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography variant="caption" color="text.secondary">Similarity To Seed</Typography>
                              <Typography variant="body2">{embeddingDetails.similarityToSeed ?? 'N/A'}</Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography variant="caption" color="text.secondary">Cluster</Typography>
                              <Typography variant="body2">{embeddingDetails.clusterId || 'N/A'}{embeddingDetails.clusterLabel ? ` (${embeddingDetails.clusterLabel})` : ''}</Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography variant="caption" color="text.secondary">Excluded From Album</Typography>
                              <Typography variant="body2">{embeddingDetails.isExcludedFromAlbum ? 'Yes' : 'No'}</Typography>
                            </Grid>
                          </Grid>
                        </Paper>

                        <Accordion disableGutters>
                          <AccordionSummary expandIcon={<ExpandMore />}>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              Token Stats
                            </Typography>
                          </AccordionSummary>
                          <AccordionDetails>
                            <Paper elevation={0} sx={{ p: 1.5, bgcolor: 'grey.100', maxHeight: 260, overflow: 'auto', fontFamily: 'monospace', fontSize: '0.75rem', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                              {JSON.stringify(embeddingDetails.tokenStats || {}, null, 2)}
                            </Paper>
                          </AccordionDetails>
                        </Accordion>

                        <Accordion disableGutters>
                          <AccordionSummary expandIcon={<ExpandMore />}>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              Features
                            </Typography>
                          </AccordionSummary>
                          <AccordionDetails>
                            <Paper elevation={0} sx={{ p: 1.5, bgcolor: 'grey.100', maxHeight: 260, overflow: 'auto', fontFamily: 'monospace', fontSize: '0.75rem', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                              {JSON.stringify(embeddingDetails.features || {}, null, 2)}
                            </Paper>
                          </AccordionDetails>
                        </Accordion>

                        <Accordion disableGutters>
                          <AccordionSummary expandIcon={<ExpandMore />}>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              Metadata
                            </Typography>
                          </AccordionSummary>
                          <AccordionDetails>
                            <Paper elevation={0} sx={{ p: 1.5, bgcolor: 'grey.100', maxHeight: 260, overflow: 'auto', fontFamily: 'monospace', fontSize: '0.75rem', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                              {JSON.stringify(embeddingDetails.metadata || {}, null, 2)}
                            </Paper>
                          </AccordionDetails>
                        </Accordion>

                        <Accordion disableGutters defaultExpanded>
                          <AccordionSummary expandIcon={<ExpandMore />}>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              Parsed Description
                            </Typography>
                          </AccordionSummary>
                          <AccordionDetails>
                            {(() => {
                              const pd = embeddingDetails.debug?.parsedDescription;
                              if (!pd) return <Typography variant="body2" color="text.secondary">No parsed description available.</Typography>;
                              return (
                                <Box>
                                  {pd.description && (
                                    <Box mb={1.5}>
                                      <Typography variant="caption" color="text.secondary">Description</Typography>
                                      <Typography variant="body2">{pd.description}</Typography>
                                    </Box>
                                  )}
                                  <Grid container spacing={1} sx={{ mb: 1.5 }}>
                                    {pd.sceneType && (
                                      <Grid item xs={6} sm={4}>
                                        <Typography variant="caption" color="text.secondary">Scene Type</Typography>
                                        <Box><Chip label={pd.sceneType} size="small" /></Box>
                                      </Grid>
                                    )}
                                    {pd.imageType && (
                                      <Grid item xs={6} sm={4}>
                                        <Typography variant="caption" color="text.secondary">Image Type</Typography>
                                        <Box><Chip label={pd.imageType} size="small" /></Box>
                                      </Grid>
                                    )}
                                    {pd.imageOrientation && (
                                      <Grid item xs={6} sm={4}>
                                        <Typography variant="caption" color="text.secondary">Orientation</Typography>
                                        <Box><Chip label={pd.imageOrientation} size="small" /></Box>
                                      </Grid>
                                    )}
                                    {pd.locationType && (
                                      <Grid item xs={6} sm={4}>
                                        <Typography variant="caption" color="text.secondary">Location Type</Typography>
                                        <Box><Chip label={pd.locationType} size="small" /></Box>
                                      </Grid>
                                    )}
                                    {pd.locationSite && pd.locationSite !== 'unknown' && (
                                      <Grid item xs={6} sm={4}>
                                        <Typography variant="caption" color="text.secondary">Location Site</Typography>
                                        <Typography variant="body2">{pd.locationSite}</Typography>
                                      </Grid>
                                    )}
                                    {pd.timeOfDay && (
                                      <Grid item xs={6} sm={4}>
                                        <Typography variant="caption" color="text.secondary">Time of Day</Typography>
                                        <Box><Chip label={pd.timeOfDay} size="small" /></Box>
                                      </Grid>
                                    )}
                                    {pd.eventType && (
                                      <Grid item xs={6} sm={4}>
                                        <Typography variant="caption" color="text.secondary">Event Type</Typography>
                                        <Box><Chip label={pd.eventType} size="small" /></Box>
                                      </Grid>
                                    )}
                                    {pd.socialContext && (
                                      <Grid item xs={6} sm={4}>
                                        <Typography variant="caption" color="text.secondary">Social Context</Typography>
                                        <Box><Chip label={pd.socialContext} size="small" /></Box>
                                      </Grid>
                                    )}
                                    {pd.activity && (
                                      <Grid item xs={12}>
                                        <Typography variant="caption" color="text.secondary">Activity</Typography>
                                        <Typography variant="body2">{pd.activity}</Typography>
                                      </Grid>
                                    )}
                                    {pd.mood && (
                                      <Grid item xs={12}>
                                        <Typography variant="caption" color="text.secondary">Mood</Typography>
                                        <Typography variant="body2">{pd.mood}</Typography>
                                      </Grid>
                                    )}
                                  </Grid>
                                  <Grid container spacing={1} sx={{ mb: 1.5 }}>
                                    <Grid item xs={4}>
                                      <Typography variant="caption" color="text.secondary">Quality Score</Typography>
                                      <Typography variant="body2">{pd.qualityScore ?? 'N/A'} / 10</Typography>
                                    </Grid>
                                    {pd.aspectRatio != null && (
                                      <Grid item xs={4}>
                                        <Typography variant="caption" color="text.secondary">Aspect Ratio</Typography>
                                        <Typography variant="body2">{pd.aspectRatio}</Typography>
                                      </Grid>
                                    )}
                                    {pd.personCount != null && (
                                      <Grid item xs={4}>
                                        <Typography variant="caption" color="text.secondary">Person Count</Typography>
                                        <Typography variant="body2">{pd.personCount}</Typography>
                                      </Grid>
                                    )}
                                    {pd.confidence != null && (
                                      <Grid item xs={4}>
                                        <Typography variant="caption" color="text.secondary">Confidence</Typography>
                                        <Typography variant="body2">{(pd.confidence * 100).toFixed(0)}%</Typography>
                                      </Grid>
                                    )}
                                  </Grid>
                                  <Box display="flex" gap={0.5} flexWrap="wrap" sx={{ mb: 1.5 }}>
                                    <Chip size="small" label={`Has Smile: ${pd.hasSmile ? 'Yes' : 'No'}`} color={pd.hasSmile ? 'success' : 'default'} variant="outlined" />
                                    <Chip size="small" label={`Blurry: ${pd.isBlurry ? 'Yes' : 'No'}`} color={pd.isBlurry ? 'error' : 'success'} variant="outlined" />
                                    <Chip size="small" label={`Album Worthy: ${pd.isAlbumWorthy ? 'Yes' : 'No'}`} color={pd.isAlbumWorthy ? 'success' : 'default'} variant="outlined" />
                                    {pd.isSelfie != null && <Chip size="small" label={`Selfie: ${pd.isSelfie ? 'Yes' : 'No'}`} variant="outlined" />}
                                  </Box>
                                  {pd.dominantColors && pd.dominantColors.length > 0 && (
                                    <Box sx={{ mb: 1.5 }}>
                                      <Typography variant="caption" color="text.secondary">Dominant Colors</Typography>
                                      <Box display="flex" gap={0.5} flexWrap="wrap" mt={0.5}>
                                        {pd.dominantColors.map((c, i) => <Chip key={i} label={c} size="small" />)}
                                      </Box>
                                    </Box>
                                  )}
                                  {pd.tags && pd.tags.length > 0 && (
                                    <Box>
                                      <Typography variant="caption" color="text.secondary">Visual Tags</Typography>
                                      <Box display="flex" gap={0.5} flexWrap="wrap" mt={0.5}>
                                        {pd.tags.map((t, i) => <Chip key={i} label={t} size="small" variant="outlined" />)}
                                      </Box>
                                    </Box>
                                  )}
                                </Box>
                              );
                            })()}
                          </AccordionDetails>
                        </Accordion>

                        <Accordion disableGutters>
                          <AccordionSummary expandIcon={<ExpandMore />}>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              Embedding Vector Preview
                            </Typography>
                          </AccordionSummary>
                          <AccordionDetails>
                            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                              Showing first 32 values out of {embeddingDetails.embedding?.length || 0}
                            </Typography>
                            <Paper elevation={0} sx={{ p: 1.5, bgcolor: 'grey.100', maxHeight: 260, overflow: 'auto', fontFamily: 'monospace', fontSize: '0.75rem', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                              {JSON.stringify((embeddingDetails.embedding || []).slice(0, 32), null, 2)}
                            </Paper>
                          </AccordionDetails>
                        </Accordion>

                        <Accordion disableGutters>
                          <AccordionSummary expandIcon={<ExpandMore />}>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              Vision Prompt
                            </Typography>
                          </AccordionSummary>
                          <AccordionDetails>
                            <Paper elevation={0} sx={{ p: 1.5, bgcolor: 'grey.100', maxHeight: 260, overflow: 'auto', fontFamily: 'monospace', fontSize: '0.75rem', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                              {embeddingDetails.debug?.visionPrompt || 'N/A'}
                            </Paper>
                          </AccordionDetails>
                        </Accordion>

                        <Accordion disableGutters>
                          <AccordionSummary expandIcon={<ExpandMore />}>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              Raw Vision Response
                            </Typography>
                          </AccordionSummary>
                          <AccordionDetails>
                            <Paper elevation={0} sx={{ p: 1.5, bgcolor: 'grey.100', maxHeight: 260, overflow: 'auto', fontFamily: 'monospace', fontSize: '0.75rem', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                              {embeddingDetails.debug?.rawVisionResponse || 'N/A'}
                            </Paper>
                          </AccordionDetails>
                        </Accordion>

                        <Accordion disableGutters>
                          <AccordionSummary expandIcon={<ExpandMore />}>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              Final Embedding Text
                            </Typography>
                          </AccordionSummary>
                          <AccordionDetails>
                            <Paper elevation={0} sx={{ p: 1.5, bgcolor: 'grey.100', maxHeight: 260, overflow: 'auto', fontFamily: 'monospace', fontSize: '0.75rem', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                              {embeddingDetails.debug?.embeddingText || 'N/A'}
                            </Paper>
                          </AccordionDetails>
                        </Accordion>

                        <Accordion disableGutters>
                          <AccordionSummary expandIcon={<ExpandMore />}>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              Full imageEmbedding Payload
                            </Typography>
                          </AccordionSummary>
                          <AccordionDetails>
                            <Paper elevation={0} sx={{ p: 1.5, bgcolor: 'grey.100', maxHeight: 320, overflow: 'auto', fontFamily: 'monospace', fontSize: '0.75rem', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                              {JSON.stringify(embeddingDetails, null, 2)}
                            </Paper>
                          </AccordionDetails>
                        </Accordion>
                      </Box>
                    )}
                  </Box>

                  {/* Face Recognition Section */}
                  {detailDialog.image.faceRecognition?.matches && detailDialog.image.faceRecognition.matches.length > 0 && (
                    <Box mt={3}>
                      <Typography variant="h6" gutterBottom>
                        <Face sx={{ mr: 1, verticalAlign: 'middle' }} />
                        Face Recognition Matches
                      </Typography>
                      
                      {(() => {
                        const matches = detailDialog.image.faceRecognition.matches;
                        const uniquePersons = [...new Set(matches.map(match => match.personName))];
                        const uniqueCount = uniquePersons.length;
                        
                        // Find the main person (highest similarity) - only if there are multiple persons
                        let mainPerson = null;
                        let highestSimilarity = 0;
                        if (uniqueCount > 1) {
                          matches.forEach(match => {
                            if ((match.similarity || 0) > highestSimilarity) {
                              highestSimilarity = match.similarity || 0;
                              mainPerson = match.personName;
                            }
                          });
                        }
                        
                        return (
                          <Box>
                            {/* Summary */}
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                              Found {uniqueCount} unique person match{uniqueCount !== 1 ? 'es' : ''} ({matches.length} total detection{matches.length !== 1 ? 's' : ''})
                            </Typography>
                            
                            {/* Search mode display */}
                            {detailDialog.image.faceRecognition.searchMode && (
                              <Box display="flex" alignItems="center" mb={2} gap={1}>
                                <Typography variant="caption" color="text.secondary">
                                  Search mode:
                                </Typography>
                                {detailDialog.image.faceRecognition.searchMode.searchMainFace && (
                                  <Chip 
                                    size="small" 
                                    variant="outlined" 
                                    color="primary"
                                    label="Main Face"
                                  />
                                )}
                                {detailDialog.image.faceRecognition.searchMode.searchAllFaces && (
                                  <Chip 
                                    size="small" 
                                    variant="outlined" 
                                    color="secondary"
                                    label="All Faces"
                                  />
                                )}
                              </Box>
                            )}
                            
                            {/* Person matches */}
                            {uniquePersons.map((personName, index) => {
                              const personMatches = matches.filter(match => match.personName === personName);
                              const bestSimilarity = Math.max(...personMatches.map(match => match.similarity || 0));
                              const isMainPerson = (uniqueCount > 1) && (personName === mainPerson);
                              
                              return (
                                <Paper key={index} elevation={1} sx={{ 
                                  p: 2, 
                                  mb: 1,
                                  border: isMainPerson ? '2px solid' : '1px solid',
                                  borderColor: isMainPerson ? 'primary.main' : 'grey.300'
                                }}>
                                  <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                                    <Box display="flex" alignItems="center" gap={1}>
                                      <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                                        {personName}
                                      </Typography>
                                      {isMainPerson && (
                                        <Chip 
                                          label="Main Person"
                                          color="primary"
                                          size="small"
                                          sx={{ fontSize: '0.65rem', height: '20px' }}
                                        />
                                      )}
                                    </Box>
                                    <Typography variant="body2" color="primary" sx={{ fontWeight: 'bold' }}>
                                      {bestSimilarity.toFixed(1)}% match
                                    </Typography>
                                  </Box>
                                  
                                  {personMatches.length > 1 && (
                                    <Typography variant="caption" color="text.secondary">
                                      {personMatches.length} face detection{personMatches.length !== 1 ? 's' : ''} found
                                    </Typography>
                                  )}
                                  
                                  {/* Show search types used for this person */}
                                  <Box display="flex" gap={0.5} mt={1}>
                                    {personMatches.some(match => match.searchType === 'main_face') && (
                                      <Chip
                                        size="small"
                                        variant="outlined"
                                        color="primary"
                                        label="Main Face"
                                        sx={{ fontSize: '0.6rem', height: '18px' }}
                                      />
                                    )}
                                    {personMatches.some(match => match.searchType === 'all_faces') && (
                                      <Chip
                                        size="small"
                                        variant="outlined"
                                        color="secondary"
                                        label="All Faces"
                                        sx={{ fontSize: '0.6rem', height: '18px' }}
                                      />
                                    )}
                                  </Box>
                                </Paper>
                              );
                            })}
                            
                            {/* Processing info */}
                            {detailDialog.image.faceRecognition.lastProcessed && (
                              <Typography variant="caption" color="text.secondary" display="block" mt={1}>
                                Last processed: {formatDate(detailDialog.image.faceRecognition.lastProcessed)}
                              </Typography>
                            )}
                          </Box>
                        );
                      })()}
                    </Box>
                  )}

                  {/* Face Detection Section - Check both top-level and nested paths */}
                  {(() => {
                    // Get faceDetection from either top-level or nested under faceRecognition
                    const faceDetection = detailDialog.image.faceDetection || detailDialog.image.faceRecognition?.faceDetection;
                    
                    return faceDetection?.processed ? (
                    <Box mt={3}>
                      <Typography variant="h6" gutterBottom>
                        <Face sx={{ mr: 1, verticalAlign: 'middle' }} />
                        Face Analysis Details
                      </Typography>
                      
                      {/* Summary Statistics */}
                      <Paper elevation={1} sx={{ p: 2, mb: 2, bgcolor: 'background.default' }}>
                        <Grid container spacing={2}>
                          <Grid item xs={6} sm={3}>
                            <Typography variant="caption" color="text.secondary">
                              Total Faces
                            </Typography>
                            <Typography variant="h6">
                              {faceDetection.totalFaces || 0}
                            </Typography>
                          </Grid>
                          
                          {faceDetection.dominantEmotion && (
                            <Grid item xs={6} sm={3}>
                              <Typography variant="caption" color="text.secondary">
                                Dominant Emotion
                              </Typography>
                              <Typography variant="body1" sx={{ fontWeight: 'medium', textTransform: 'capitalize' }}>
                                {faceDetection.dominantEmotion.toLowerCase()}
                              </Typography>
                            </Grid>
                          )}
                          
                          {faceDetection.averageAge && (
                            <Grid item xs={6} sm={3}>
                              <Typography variant="caption" color="text.secondary">
                                Average Age
                              </Typography>
                              <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                                {faceDetection.averageAge} years
                              </Typography>
                            </Grid>
                          )}
                          
                          {faceDetection.averageQuality && (
                            <Grid item xs={6} sm={3}>
                              <Typography variant="caption" color="text.secondary">
                                Quality Score
                              </Typography>
                              <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                                {((faceDetection.averageQuality.brightness + 
                                   faceDetection.averageQuality.sharpness) / 2).toFixed(1)}%
                              </Typography>
                            </Grid>
                          )}
                        </Grid>
                        
                        {/* Quick indicators */}
                        <Box display="flex" gap={1} mt={2} flexWrap="wrap">
                          {faceDetection.hasSmiles && (
                            <Chip
                              size="small"
                              color="success"
                              variant="outlined"
                              label="Has Smiles"
                              sx={{ fontSize: '0.7rem' }}
                            />
                          )}
                          {faceDetection.allEyesOpen && (
                            <Chip
                              size="small"
                              color="success"
                              variant="outlined"
                              label="All Eyes Open"
                              sx={{ fontSize: '0.7rem' }}
                            />
                          )}
                        </Box>
                      </Paper>
                      
                      {/* Individual Face Details */}
                      {faceDetection.faces && faceDetection.faces.length > 0 && (
                        <Box>
                          <Typography variant="subtitle2" gutterBottom sx={{ mt: 2, mb: 1 }}>
                            Individual Face Analysis
                          </Typography>
                          
                          {faceDetection.faces.map((face, index) => (
                            <Paper key={index} elevation={2} sx={{ p: 2, mb: 2, border: '1px solid', borderColor: 'divider' }}>
                              <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                                  Face {index + 1}
                                  {face.faceIndex !== undefined && (
                                    <Typography variant="caption" color="text.secondary" component="span" sx={{ ml: 1 }}>
                                      (Index: {face.faceIndex})
                                    </Typography>
                                  )}
                                  {face.matchedPerson && (
                                    <Chip
                                      label={face.matchedPerson.name}
                                      color="primary"
                                      size="small"
                                      sx={{ ml: 1, fontSize: '0.7rem', height: '20px' }}
                                    />
                                  )}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  Confidence: {face.confidence?.toFixed(1)}%
                                </Typography>
                              </Box>
                              
                              {/* Bounding Box Information */}
                              {face.boundingBox && (
                                <Box mb={2}>
                                  <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                                    Bounding Box
                                  </Typography>
                                  <Box display="flex" gap={2} flexWrap="wrap">
                                    <Typography variant="caption">
                                      Width: {(face.boundingBox.Width * 100).toFixed(1)}%
                                    </Typography>
                                    <Typography variant="caption">
                                      Height: {(face.boundingBox.Height * 100).toFixed(1)}%
                                    </Typography>
                                    <Typography variant="caption">
                                      Left: {(face.boundingBox.Left * 100).toFixed(1)}%
                                    </Typography>
                                    <Typography variant="caption">
                                      Top: {(face.boundingBox.Top * 100).toFixed(1)}%
                                    </Typography>
                                  </Box>
                                </Box>
                              )}
                              
                              <Grid container spacing={2}>
                                {/* Age & Gender */}
                                {face.ageRange && (
                                  <Grid item xs={6} sm={4}>
                                    <Typography variant="caption" color="text.secondary" display="block">
                                      Age Range
                                    </Typography>
                                    <Typography variant="body2">
                                      {face.ageRange.low}-{face.ageRange.high} years
                                    </Typography>
                                  </Grid>
                                )}
                                
                                {face.gender && (
                                  <Grid item xs={6} sm={4}>
                                    <Typography variant="caption" color="text.secondary" display="block">
                                      Gender
                                    </Typography>
                                    <Typography variant="body2">
                                      {face.gender.value} ({face.gender.confidence?.toFixed(1)}%)
                                    </Typography>
                                  </Grid>
                                )}
                                
                                {/* Emotions */}
                                {face.emotions && face.emotions.length > 0 && (
                                  <Grid item xs={12}>
                                    <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                                      Emotions
                                    </Typography>
                                    <Box display="flex" gap={0.5} flexWrap="wrap">
                                      {face.emotions.slice(0, 3).map((emotion, i) => (
                                        <Chip
                                          key={i}
                                          label={`${emotion.type} ${emotion.confidence?.toFixed(0)}%`}
                                          size="small"
                                          variant={i === 0 ? "filled" : "outlined"}
                                          color={i === 0 ? "primary" : "default"}
                                          sx={{ fontSize: '0.65rem', textTransform: 'capitalize' }}
                                        />
                                      ))}
                                    </Box>
                                  </Grid>
                                )}
                                
                                {/* Facial Attributes */}
                                <Grid item xs={12}>
                                  <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                                    Attributes
                                  </Typography>
                                  <Box display="flex" gap={0.5} flexWrap="wrap">
                                    {face.smile && (
                                      <Chip
                                        label={`Smile: ${face.smile.value ? 'Yes' : 'No'} (${face.smile.confidence?.toFixed(1)}%)`}
                                        size="small"
                                        color={face.smile.value ? "success" : "default"}
                                        variant="outlined"
                                        sx={{ fontSize: '0.65rem' }}
                                      />
                                    )}
                                    {face.eyeglasses && (
                                      <Chip
                                        label={`Eyeglasses: ${face.eyeglasses.value ? 'Yes' : 'No'} (${face.eyeglasses.confidence?.toFixed(1)}%)`}
                                        size="small"
                                        color={face.eyeglasses.value ? "primary" : "default"}
                                        variant="outlined"
                                        sx={{ fontSize: '0.65rem' }}
                                      />
                                    )}
                                    {face.sunglasses && (
                                      <Chip
                                        label={`Sunglasses: ${face.sunglasses.value ? 'Yes' : 'No'} (${face.sunglasses.confidence?.toFixed(1)}%)`}
                                        size="small"
                                        color={face.sunglasses.value ? "primary" : "default"}
                                        variant="outlined"
                                        sx={{ fontSize: '0.65rem' }}
                                      />
                                    )}
                                    {face.beard && (
                                      <Chip
                                        label={`Beard: ${face.beard.value ? 'Yes' : 'No'} (${face.beard.confidence?.toFixed(1)}%)`}
                                        size="small"
                                        color={face.beard.value ? "primary" : "default"}
                                        variant="outlined"
                                        sx={{ fontSize: '0.65rem' }}
                                      />
                                    )}
                                    {face.mustache && (
                                      <Chip
                                        label={`Mustache: ${face.mustache.value ? 'Yes' : 'No'} (${face.mustache.confidence?.toFixed(1)}%)`}
                                        size="small"
                                        color={face.mustache.value ? "primary" : "default"}
                                        variant="outlined"
                                        sx={{ fontSize: '0.65rem' }}
                                      />
                                    )}
                                    {face.eyesOpen && (
                                      <Chip
                                        label={`Eyes Open: ${face.eyesOpen.value ? 'Yes' : 'No'} (${face.eyesOpen.confidence?.toFixed(1)}%)`}
                                        size="small"
                                        color={face.eyesOpen.value ? "success" : "default"}
                                        variant="outlined"
                                        sx={{ fontSize: '0.65rem' }}
                                      />
                                    )}
                                    {face.mouthOpen && (
                                      <Chip
                                        label={`Mouth Open: ${face.mouthOpen.value ? 'Yes' : 'No'} (${face.mouthOpen.confidence?.toFixed(1)}%)`}
                                        size="small"
                                        color={face.mouthOpen.value ? "primary" : "default"}
                                        variant="outlined"
                                        sx={{ fontSize: '0.65rem' }}
                                      />
                                    )}
                                  </Box>
                                </Grid>
                                
                                {/* Quality Metrics */}
                                {face.quality && (
                                  <Grid item xs={12}>
                                    <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                                      Quality Metrics
                                    </Typography>
                                    <Box display="flex" gap={2}>
                                      {face.quality.brightness !== undefined && (
                                        <Box>
                                          <Typography variant="caption" color="text.secondary">
                                            Brightness: 
                                          </Typography>
                                          <Typography variant="body2" component="span" sx={{ ml: 0.5 }}>
                                            {face.quality.brightness.toFixed(1)}%
                                          </Typography>
                                        </Box>
                                      )}
                                      {face.quality.sharpness !== undefined && (
                                        <Box>
                                          <Typography variant="caption" color="text.secondary">
                                            Sharpness: 
                                          </Typography>
                                          <Typography variant="body2" component="span" sx={{ ml: 0.5 }}>
                                            {face.quality.sharpness.toFixed(1)}%
                                          </Typography>
                                        </Box>
                                      )}
                                    </Box>
                                  </Grid>
                                )}
                                
                                {/* Pose Information */}
                                {face.pose && (face.pose.roll !== undefined || face.pose.yaw !== undefined || face.pose.pitch !== undefined) && (
                                  <Grid item xs={12}>
                                    <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                                      Head Pose
                                    </Typography>
                                    <Box display="flex" gap={2}>
                                      {face.pose.roll !== undefined && (
                                        <Typography variant="caption">
                                          Roll: {face.pose.roll.toFixed(1)}°
                                        </Typography>
                                      )}
                                      {face.pose.yaw !== undefined && (
                                        <Typography variant="caption">
                                          Yaw: {face.pose.yaw.toFixed(1)}°
                                        </Typography>
                                      )}
                                      {face.pose.pitch !== undefined && (
                                        <Typography variant="caption">
                                          Pitch: {face.pose.pitch.toFixed(1)}°
                                        </Typography>
                                      )}
                                    </Box>
                                  </Grid>
                                )}
                              </Grid>
                            </Paper>
                          ))}
                        </Box>
                      )}
                      
                      {/* Processing timestamp */}
                      {faceDetection.processedAt && (
                        <Typography variant="caption" color="text.secondary" display="block" mt={1}>
                          Analysis performed: {formatDate(faceDetection.processedAt)}
                        </Typography>
                      )}
                    </Box>
                  ) : null;
                  })()}
                  
                  {/* Show info message if processed but no enhanced detection data */}
                  {!detailDialog.image.faceDetection && !detailDialog.image.faceRecognition?.faceDetection && detailDialog.image.faceRecognition?.awsProcessed && (
                    <Box mt={3}>
                      <Paper elevation={1} sx={{ p: 2, bgcolor: 'info.lighter', border: '1px solid', borderColor: 'info.light' }}>
                        <Typography variant="body2" color="info.main" gutterBottom>
                          <Face sx={{ mr: 1, verticalAlign: 'middle', fontSize: '1.2rem' }} />
                          <strong>Enhanced Face Analysis Available</strong>
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          This image was processed before the enhanced face analysis feature was added.
                          To see detailed face attributes (age, gender, emotions, quality metrics, etc.),
                          please run face recognition again on this session.
                        </Typography>
                      </Paper>
                    </Box>
                  )}
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => handleDownload(detailDialog.image)} startIcon={<Download />}>
                Download
              </Button>
              {canEdit && (
                <Button
                  onClick={() => {
                    setDetailDialog({ open: false, image: null });
                    handleEdit(detailDialog.image);
                  }}
                  startIcon={<Edit />}
                >
                  Edit
                </Button>
              )}
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Edit Dialog */}
      <Dialog
        open={editDialog.open}
        onClose={() => {
          setEditDialog({ open: false, image: null });
          setEditFormData({ name: '', description: '', tags: '' });
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Image</DialogTitle>
        <DialogContent>
          {editDialog.image && (
            <>
              <Box mb={2}>
                <AuthenticatedImage
                  imageId={editDialog.image.uniqueId}
                  alt={editDialog.image.name}
                  style={{
                    width: '100%',
                    maxHeight: '200px',
                    objectFit: 'contain',
                    borderRadius: '4px'
                  }}
                />
              </Box>
              <TextField
                fullWidth
                label="Name"
                value={editFormData.name}
                onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
                margin="normal"
              />
              <TextField
                fullWidth
                label="Description"
                value={editFormData.description}
                onChange={(e) => setEditFormData({ ...editFormData, description: e.target.value })}
                margin="normal"
                multiline
                rows={3}
              />
              <TextField
                fullWidth
                label="Tags"
                value={editFormData.tags}
                onChange={(e) => setEditFormData({ ...editFormData, tags: e.target.value })}
                margin="normal"
                helperText="Separate tags with commas"
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setEditDialog({ open: false, image: null });
            setEditFormData({ name: '', description: '', tags: '' });
          }}>
            Cancel
          </Button>
          <Button variant="contained" onClick={saveEdit}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, image: null, bulkDelete: false, selectedCount: 0 })}
        maxWidth="sm"
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            {deleteDialog.bulkDelete ? (
              <>
                Are you sure you want to delete <strong>{deleteDialog.selectedCount} selected image{deleteDialog.selectedCount !== 1 ? 's' : ''}</strong>? This action cannot be undone.
              </>
            ) : (
              <>
                Are you sure you want to delete "<strong>{deleteDialog.image?.name}</strong>"? This action cannot be undone.
              </>
            )}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteDialog({ open: false, image: null, bulkDelete: false, selectedCount: 0 })}
            disabled={deleting}
          >
            Cancel
          </Button>
          <Button 
            onClick={deleteDialog.bulkDelete ? confirmBulkDelete : confirmDelete} 
            color="error" 
            variant="contained"
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {deleting ? 'Deleting...' : `Delete ${deleteDialog.bulkDelete ? `${deleteDialog.selectedCount} Image${deleteDialog.selectedCount !== 1 ? 's' : ''}` : 'Image'}`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Image Analysis Dialog */}
      <Dialog
        open={analysisDialog.open}
        onClose={closeAnalysisDialog}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center">
            <Psychology sx={{ mr: 1 }} />
            AI Image Analysis ({selectedImageIds.length} images)
          </Box>
        </DialogTitle>
        <DialogContent>
          {!analysisDialog.results && !analysisDialog.loading && (
            <Box mb={3}>
              <Typography variant="body1" gutterBottom>
                Analyze selected images using AI vision. Choose your provider and optionally provide a custom prompt.
              </Typography>
              
              <FormControl fullWidth sx={{ mt: 2, mb: 2 }}>
                <FormLabel id="vision-provider-label">Analysis Provider</FormLabel>
                <RadioGroup
                  row
                  aria-labelledby="vision-provider-label"
                  name="vision-provider"
                  value={analysisProvider}
                  onChange={(e) => setAnalysisProvider(e.target.value)}
                >
                  <FormControlLabel 
                    value="openai" 
                    control={<Radio />} 
                    label="OpenAI Vision" 
                    disabled={!analysisServiceStatus?.openai?.available} 
                  />
                  <FormControlLabel 
                    value="azure" 
                    control={<Radio />} 
                    label="Azure Vision" 
                    disabled={!analysisServiceStatus?.azure?.available} 
                  />
                </RadioGroup>
              </FormControl>
              <TextField
                fullWidth
                label="Custom Analysis Prompt (optional)"
                multiline
                rows={4}
                value={analysisPrompt}
                onChange={(e) => setAnalysisPrompt(e.target.value)}
                placeholder="Enter a custom prompt for analysis, or leave empty for comprehensive analysis"
                sx={{ mt: 2 }}
              />
              <Alert severity="info" sx={{ mt: 2 }}>
                <Typography variant="body2">
                  Default analysis includes: main subject, scene description, objects, colors, text content, mood, and technical aspects.
                </Typography>
              </Alert>
            </Box>
          )}

          {analysisDialog.loading && (
            <Box display="flex" flexDirection="column" alignItems="center" py={4}>
              <CircularProgress size={60} />
              <Typography variant="h6" sx={{ mt: 2 }}>
                Analyzing Images...
              </Typography>
              <Typography variant="body2" color="text.secondary">
                This may take a few moments
              </Typography>
            </Box>
          )}

          {analysisDialog.results && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Analysis Results
              </Typography>
              <Box 
                sx={{ 
                  p: 2, 
                  mb: 2, 
                  backgroundColor: analysisDialog.results.total > 1 ? 'success.light' : 'grey.100', 
                  borderRadius: 1 
                }}
              >
                <Typography variant="body1" fontWeight="bold">
                  Successfully analyzed {analysisDialog.results.analyzed} of {analysisDialog.results.total} images
                  {analysisDialog.results.failed > 0 && ` (${analysisDialog.results.failed} failed)`}
                </Typography>
                {analysisDialog.results.total > 1 && (
                  <Typography variant="body2" color="text.secondary">
                    Each image has been analyzed separately below. Expand any section to view the full analysis.
                  </Typography>
                )}
              </Box>

              {/* Analysis Results */}
              {analysisDialog.results.results?.map((result, index) => (
                <Accordion key={result.id} defaultExpanded={true}>
                  <AccordionSummary expandIcon={<ExpandMore />}>
                    <Box display="flex" alignItems="center" width="100%">
                      <AuthenticatedImage
                        imageId={result.id}
                        type="thumbnail"
                        fallbackToServe={true}
                        alt={result.name || result.originalName}
                        sx={{
                          width: 40,
                          height: 40,
                          objectFit: 'cover',
                          borderRadius: 1,
                          mr: 2
                        }}
                      />
                      <Box flex={1}>
                        <Typography variant="subtitle2">
                          {result.name || result.originalName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {result.success ? 'Analysis completed' : 'Analysis failed'}
                        </Typography>
                      </Box>
                      <Chip 
                        label={result.success ? 'Analyzed' : 'Failed'} 
                        color={result.success ? 'success' : 'error'} 
                        size="small"
                        variant="outlined"
                      />
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    {result.success ? (
                      <Box>
                        <TextField
                          fullWidth
                          multiline
                          value={result.analysis}
                          variant="outlined"
                          inputProps={{ readOnly: true }}
                          sx={{
                            '& .MuiInputBase-root': {
                              backgroundColor: 'grey.50'
                            }
                          }}
                        />
                        {result.metadata && (
                          <Box mt={2}>
                            <Typography variant="caption" color="text.secondary">
                              Model: {result.metadata.model} | 
                              Tokens used: {result.metadata.tokensUsed} | 
                              Time: {new Date(result.metadata.timestamp).toLocaleString()}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    ) : (
                      <Alert severity="error">
                        <Typography variant="body2">
                          {result.error}
                        </Typography>
                      </Alert>
                    )}
                  </AccordionDetails>
                </Accordion>
              ))}

              {/* Analysis Errors */}
              {analysisDialog.results.errors?.length > 0 && (
                <Box mt={3}>
                  <Typography variant="h6" color="error" gutterBottom>
                    Failed Analyses
                  </Typography>
                  {analysisDialog.results.errors.map((error, index) => (
                    <Alert key={index} severity="error" sx={{ mb: 1 }}>
                      <Typography variant="body2">
                        <strong>{error.name}:</strong> {error.error}
                      </Typography>
                    </Alert>
                  ))}
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeAnalysisDialog}>
            {analysisDialog.results ? 'Close' : 'Cancel'}
          </Button>
          {!analysisDialog.results && !analysisDialog.loading && (
            <Button 
              onClick={performImageAnalysis} 
              variant="contained"
              startIcon={<Psychology />}
            >
              Start Analysis
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Face Recognition Dialog */}
      <Dialog
        open={faceRecognitionDialog.open}
        onClose={faceRecognitionDialog.loading ? undefined : closeFaceRecognitionDialog}
        aria-labelledby="face-recognition-dialog-title"
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center">
            <Face sx={{ mr: 1 }} />
            Face Recognition ({selectedImageIds.length} images)
          </Box>
        </DialogTitle>
        <DialogContent>
          {!faceRecognitionDialog.results && !faceRecognitionDialog.loading && (
            <Box mb={3}>
              <Typography variant="body1" gutterBottom>
                Recognize and group faces across your selected images using Azure Face API.
              </Typography>
              
              <FormControlLabel
                control={
                  <Checkbox
                    checked={includeAttributes}
                    onChange={(e) => setIncludeAttributes(e.target.checked)}
                  />
                }
                label="Include face attributes (age, gender, emotion)"
              />
              
              <Box mt={3} mb={2}>
                <Alert severity="info">
                  Face recognition will detect faces in your images and group similar faces together. 
                  This can help you find the same person across multiple photos.
                </Alert>
              </Box>
            </Box>
          )}
          
          {faceRecognitionDialog.loading && (
            <Box display="flex" flexDirection="column" alignItems="center" my={4}>
              <CircularProgress size={60} />
              <Typography variant="h6" sx={{ mt: 2 }}>
                Recognizing faces...
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                This may take a few moments depending on the number of images
              </Typography>
            </Box>
          )}
          
          {faceRecognitionDialog.results && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Face Recognition Results
              </Typography>
              
              <Typography variant="body1" paragraph>
                Processed {faceRecognitionDialog.results.processedImages} images and found {faceRecognitionDialog.results.totalFaces} faces
              </Typography>
              
              {faceRecognitionDialog.results.totalFaces > 0 ? (
                <>
                  <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                    Face Groups ({faceRecognitionDialog.results.faceGroups?.length || 0})
                  </Typography>
                  
                  <Grid container spacing={3}>
                    {faceRecognitionDialog.results.faceGroups?.map((group, groupIndex) => (
                      <Grid item xs={12} sm={6} md={4} key={`group-${groupIndex}`}>
                        <Paper elevation={2} sx={{ p: 2 }}>
                          <Typography variant="subtitle1" gutterBottom>
                            Person {groupIndex + 1} - {group.faces.length} photos
                          </Typography>
                          
                          <Grid container spacing={1}>
                            {group.faces.map((face, faceIndex) => (
                              <Grid item xs={4} key={`face-${groupIndex}-${faceIndex}`}>
                                <AuthenticatedImage
                                  src={`${API_BASE_URL}/api/images/face/${face.faceId}`}
                                  alt={`Face ${faceIndex + 1}`}
                                  style={{ width: '100%', height: 'auto', borderRadius: '4px' }}
                                />
                                {includeAttributes && face.attributes && (
                                  <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                                    {face.attributes.age ? `Age: ~${face.attributes.age}` : ''}
                                    {face.attributes.gender ? `, ${face.attributes.gender}` : ''}
                                  </Typography>
                                )}
                              </Grid>
                            ))}
                          </Grid>
                        </Paper>
                      </Grid>
                    ))}
                  </Grid>
                </>
              ) : (
                <Alert severity="info">No faces were detected in the selected images</Alert>
              )}
            </Box>
          )}
        </DialogContent>
        
        <DialogActions>
          {!faceRecognitionDialog.loading && !faceRecognitionDialog.results && (
            <>
              <Button onClick={closeFaceRecognitionDialog}>Cancel</Button>
              <Button 
                onClick={performFaceRecognition} 
                variant="contained" 
                color="primary" 
                startIcon={<Face />}
              >
                Start Face Recognition
              </Button>
            </>
          )}
          
          {faceRecognitionDialog.results && (
            <Button onClick={closeFaceRecognitionDialog}>Close</Button>
          )}
        </DialogActions>
      </Dialog>

      {/* AWS Face Recognition Dialog */}
      <Dialog
        open={awsFaceDialog.open}
        onClose={closeAwsFaceDialog}
        aria-labelledby="aws-face-dialog-title"
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center">
              <CloudOutlined sx={{ mr: 1 }} />
              AWS Face Recognition - {selectedImageIds.length > 0 ? `${selectedImageIds.length} Selected Images` : `All Images (${totalImages})`} (Session: {currentSession?.name || 'None'})
            </Box>
            <Box display="flex" alignItems="center" gap={1}>
              {awsFaceDialog.loading && (
                <Chip 
                  label="In Progress" 
                  color="primary" 
                  size="small"
                  sx={{ fontWeight: 'bold' }}
                />
              )}
              <IconButton
                onClick={closeAwsFaceDialog}
                size="small"
                aria-label="Close AWS Face Recognition dialog"
              >
                <Close />
              </IconButton>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent>
          {stuckFaceOperation && (
            <Alert
              severity="warning"
              sx={{ mb: 2 }}
              action={
                <Button color="warning" size="small" variant="outlined" onClick={handleForceResetAndRetryFace}>
                  Force Reset &amp; Retry
                </Button>
              }
            >
              A previous face recognition run is still marked as <strong>in progress</strong> in the
              database (the backend may have restarted mid-run). Click{' '}
              <strong>Force Reset &amp; Retry</strong> to clear it and start a new run.
            </Alert>
          )}
          {!awsFaceDialog.results && !awsFaceDialog.loading && (
            <Box mb={3}>
              <Typography variant="body1" gutterBottom>
                Search for faces matching your reference persons using AWS Rekognition on {selectedImageIds.length > 0 ? `${selectedImageIds.length} selected` : `all ${totalImages}`} photos.
              </Typography>
              
              <Alert severity="info" sx={{ mt: 2, mb: 3 }}>
                This will search {selectedImageIds.length > 0 ? `the ${selectedImageIds.length} selected photos` : `all ${totalImages} photos in this session`} for faces that match the reference persons you have already defined. 
                If you haven't added any reference persons yet, please use the face recognition tools first to add some reference persons.
              </Alert>

              {/* AWS Face Search Options */}
              <Box sx={{ mt: 3, mb: 3, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Search Options
                </Typography>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={awsFaceOptions.mainFacesOnly}
                      onChange={(e) => setAwsFaceOptions(prev => ({ ...prev, mainFacesOnly: e.target.checked }))}
                      size="small"
                    />
                  }
                  label="Main faces only (skip small/background faces)"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={awsFaceOptions.showAllFaces}
                      onChange={(e) => setAwsFaceOptions(prev => ({ ...prev, showAllFaces: e.target.checked }))}
                      size="small"
                    />
                  }
                  label="Show all detected faces (not just matches)"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={awsFaceOptions.overrideProcessed}
                      onChange={(e) => setAwsFaceOptions(prev => ({ ...prev, overrideProcessed: e.target.checked }))}
                      size="small"
                      color="warning"
                    />
                  }
                  label={
                    <Box component="span">
                      <Typography component="span" variant="body2">
                        Override existing results
                      </Typography>
                      <Typography component="span" variant="caption" color="textSecondary" display="block">
                        Reprocess images that have already been analyzed
                      </Typography>
                    </Box>
                  }
                />
              </Box>

              <Box mt={3}>
                <Typography variant="subtitle1" gutterBottom>
                  How it works:
                </Typography>
                <Typography variant="body2" component="div">
                  <ul>
                    <li>AWS Rekognition will use your existing reference persons from this session</li>
                    <li>It will scan {selectedImageIds.length > 0 ? `the ${selectedImageIds.length} selected photos` : 'all photos in the session'} looking for matching faces</li>
                    <li>Results will be organized by reference person</li>
                    <li>Each match includes similarity percentage and confidence level</li>
                  </ul>
                </Typography>
              </Box>
            </Box>
          )}
          
          {awsFaceDialog.showProgress ? (
            <SimpleProgressDisplay
              progressData={awsFaceDialog.progressData}
              title="AWS Face Recognition"
              description="Searching for faces matching your reference persons..."
            />
          ) : awsFaceDialog.loading ? (
            <Box display="flex" flexDirection="column" alignItems="center" my={4}>
              <CircularProgress size={60} />
              <Typography variant="h6" sx={{ mt: 2 }}>
                Searching for matching faces...
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                AWS Rekognition is comparing reference persons with {selectedImageIds.length > 0 ? `${selectedImageIds.length} selected photos` : 'all faces in the session'}
              </Typography>
            </Box>
          ) : null}
          
          {awsFaceDialog.results && (
            <Box>
              <Typography variant="h6" gutterBottom>
                AWS Face Recognition Results
              </Typography>
              
              {/* Processing statistics */}
              {(awsFaceDialog.results.processedImages > 0 || awsFaceDialog.results.alreadyProcessedCount > 0 || awsFaceDialog.progressData) && (
                <Alert severity="success" sx={{ mb: 2 }}>
                  <Typography variant="body2">
                    <strong>Processing Statistics:</strong><br />
                    • Images analyzed: {awsFaceDialog.results.processedImages || awsFaceDialog.progressData?.processedCount || 0} / {awsFaceDialog.progressData?.totalCount || (awsFaceDialog.results.processedImages + awsFaceDialog.results.alreadyProcessedCount) || 0}<br />
                    {awsFaceDialog.results.alreadyProcessedCount > 0 && (
                      <>• Skipped (already processed): {awsFaceDialog.results.alreadyProcessedCount}<br /></>
                    )}
                    • Matches found: {awsFaceDialog.results.matches?.length || 0}<br />
                    {awsFaceDialog.progressData?.totalProcessingTime > 0 && (
                      <>
                        • Total processing time: {(() => {
                          const totalMs = awsFaceDialog.progressData.totalProcessingTime;
                          if (totalMs < 60000) return `${(totalMs / 1000).toFixed(1)}s`;
                          const totalSeconds = Math.floor(totalMs / 1000);
                          const minutes = Math.floor(totalSeconds / 60);
                          const seconds = totalSeconds % 60;
                          return `${minutes}:${seconds.toString().padStart(2, '0')}`;
                        })()}<br />
                        • Average time per image: {(() => {
                          const avgMs = awsFaceDialog.progressData.averageTime;
                          if (avgMs < 1000) return `${avgMs}ms`;
                          return `${(avgMs / 1000).toFixed(1)}s`;
                        })()}<br />
                      </>
                    )}
                  </Typography>
                </Alert>
              )}
              
              {awsFaceDialog.results.referencePersons?.length > 0 ? (
                <Typography variant="body1" paragraph>
                  Searched {selectedImageIds.length > 0 ? `${selectedImageIds.length} selected` : `all ${totalImages}`} photos and found {awsFaceDialog.results.matches?.length || 0} total matches across {awsFaceDialog.results.referencePersons.length} reference persons
                </Typography>
              ) : (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  No reference persons found in this session. Please add reference persons first using the face recognition tools.
                </Alert>
              )}
              
              {awsFaceDialog.results.matches?.length > 0 ? (
                <>
                  {/* Group results by selected image */}
                  {(() => {
                    // Create map of selected images and their matches
                    const matchesByImage = {};
                    const processedImages = new Set();
                    
                    // Initialize all selected images (even those without matches)
                    selectedImageIds.forEach(imageId => {
                      const image = images.find(img => img.uniqueId === imageId);
                      if (image) {
                        matchesByImage[imageId] = {
                          imageName: image.name || image.originalName || `Image ${imageId.slice(-8)}`,
                          matches: []
                        };
                      }
                    });
                    
                    // Group matches by image
                    awsFaceDialog.results.matches.forEach(match => {
                      if (matchesByImage[match.imageId]) {
                        matchesByImage[match.imageId].matches.push(match);
                        processedImages.add(match.imageId);
                      }
                    });
                    
                    return (
                      <Box>
                        <Typography variant="h6" gutterBottom sx={{ mt: 2, mb: 2 }}>
                          Results by Selected Photo:
                        </Typography>
                        
                        {Object.entries(matchesByImage).map(([imageId, imageData]) => {
                          const matches = imageData.matches;
                          const hasMatches = matches.length > 0;
                          
                          // Find main person (highest similarity) and unique persons
                          const uniquePersons = [];
                          const personSimilarities = {};
                          
                          matches.forEach(match => {
                            if (!personSimilarities[match.personName] || 
                                match.similarity > personSimilarities[match.personName].similarity) {
                              personSimilarities[match.personName] = match;
                            }
                          });
                          
                          Object.values(personSimilarities).forEach(match => {
                            uniquePersons.push(match);
                          });
                          
                          // Sort by similarity (highest first)
                          uniquePersons.sort((a, b) => b.similarity - a.similarity);
                          const mainPerson = uniquePersons[0];
                          const otherPersons = uniquePersons.slice(1);
                          
                          return (
                            <Paper key={imageId} elevation={2} sx={{ p: 2, mb: 2 }}>
                              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                                <Box display="flex" alignItems="center" gap={2}>
                                  {/* Photo thumbnail */}
                                  <Box sx={{ minWidth: 80, minHeight: 60 }}>
                                    <AuthenticatedImage
                                      imageId={imageId}
                                      alt={imageData.imageName}
                                      style={{ 
                                        width: '80px', 
                                        height: '60px', 
                                        borderRadius: '8px', 
                                        objectFit: 'cover',
                                        border: '2px solid #e0e0e0'
                                      }}
                                    />
                                  </Box>
                                  <Typography variant="h6" color="primary">
                                    📷 {imageData.imageName}
                                  </Typography>
                                </Box>
                                <Chip 
                                  label={hasMatches ? `${uniquePersons.length} unique match${uniquePersons.length !== 1 ? 'es' : ''}` : 'No matches'}
                                  color={hasMatches ? "success" : "default"}
                                  size="small"
                                />
                              </Box>
                              
                              {hasMatches ? (
                                <Box>
                                  {/* Main person match */}
                                  {mainPerson && (
                                    <Box sx={{ mb: 2, p: 2, bgcolor: 'primary.main', color: 'white', borderRadius: 1 }}>
                                      <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                                        👤 Main Match: {mainPerson.personName}
                                      </Typography>
                                      <Box display="flex" alignItems="center" gap={2}>
                                        <Chip 
                                          label={`${Math.round(mainPerson.similarity)}% similar`}
                                          sx={{ bgcolor: 'white', color: 'primary.main' }}
                                          size="small"
                                        />
                                        <Typography variant="body2">
                                          Confidence: {Math.round(mainPerson.confidence)}%
                                        </Typography>
                                      </Box>
                                    </Box>
                                  )}
                                  
                                  {/* Other unique matches */}
                                  {otherPersons.length > 0 && (
                                    <Box sx={{ mb: 2 }}>
                                      <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold', color: 'text.secondary' }}>
                                        👥 Other Matches:
                                      </Typography>
                                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                        {otherPersons.map((match, index) => (
                                          <Paper key={index} elevation={1} sx={{ p: 1.5, flex: '1', minWidth: '200px' }}>
                                            <Box display="flex" justifyContent="space-between" alignItems="center">
                                              <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                                {match.personName}
                                              </Typography>
                                              <Box display="flex" flexDirection="column" alignItems="end" gap={0.5}>
                                                <Chip 
                                                  label={`${Math.round(match.similarity)}%`}
                                                  color="secondary"
                                                  size="small"
                                                />
                                                <Typography variant="caption" color="text.secondary">
                                                  {Math.round(match.confidence)}% conf.
                                                </Typography>
                                              </Box>
                                            </Box>
                                          </Paper>
                                        ))}
                                      </Box>
                                    </Box>
                                  )}
                                </Box>
                              ) : (
                                <Typography variant="body2" color="text.secondary">
                                  No matching faces found in this photo
                                </Typography>
                              )}
                              
                              {/* View image button - always show */}
                              <Box sx={{ mt: 2, textAlign: 'center' }}>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  startIcon={viewImageLoading.has(imageId) ? <CircularProgress size={16} /> : <Visibility />}
                                  disabled={viewImageLoading.has(imageId)}
                                  onClick={async () => {
                                    try {
                                      // Set loading state for this specific image
                                      setViewImageLoading(prev => new Set([...prev, imageId]));
                                      
                                      // First refresh the face recognition data for this image
                                      await updateSingleImage(imageId);
                                      
                                      // Then get the updated image and show it
                                      const updatedImage = images.find(img => img.uniqueId === imageId);
                                      if (updatedImage) {
                                        setDetailDialog({ open: true, image: updatedImage });
                                      }
                                    } catch (error) {
                                      console.error('Failed to refresh image data:', error);
                                      showSnackbar('Failed to refresh image data, showing current data', 'warning');
                                      // Fallback to showing current data
                                      const image = images.find(img => img.uniqueId === imageId);
                                      if (image) {
                                        setDetailDialog({ open: true, image });
                                      }
                                    } finally {
                                      // Clear loading state for this specific image
                                      setViewImageLoading(prev => {
                                        const newSet = new Set(prev);
                                        newSet.delete(imageId);
                                        return newSet;
                                      });
                                    }
                                  }}
                                >
                                  {viewImageLoading.has(imageId) ? 'Refreshing...' : 'View Full Image'}
                                </Button>
                              </Box>
                            </Paper>
                          );
                        })}
                      </Box>
                    );
                  })()}
                </>
              ) : (
                <Alert severity="info">No matching faces were found in the selected photos</Alert>
              )}
            </Box>
          )}
        </DialogContent>
        
        <DialogActions>
          {!awsFaceDialog.loading && !awsFaceDialog.results && (
            <>
              <Button onClick={closeAwsFaceDialog}>Cancel</Button>
              <Button 
                onClick={performAwsFaceRecognition}
                variant="contained"
                color="primary"
                startIcon={<CloudOutlined />}
              >
                Start AWS Face Search
              </Button>
            </>
          )}
          
          {awsFaceDialog.loading && (
            <Button 
              onClick={cancelAwsFaceRecognition}
              variant="outlined"
              color="error"
            >
              Cancel Operation
            </Button>
          )}
          
          {awsFaceDialog.results && (
            <Button onClick={closeAwsFaceDialog}>Close</Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={closeSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={closeSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Session Selection Dialog */}
      <SessionSelectionDialog
        open={sessionDialog.open}
        onClose={() => setSessionDialog({ open: false })}
        onSessionSelected={handleSessionSelected}
        onSessionDeleted={handleSessionDeleted}
        title="Select or Create a Session"
        refreshTrigger={sessionRefreshTrigger}
      />

      {/* Google Photos Dialog */}
      <GooglePhotosDialog
        open={googlePhotosDialog.open}
        onClose={() => setGooglePhotosDialog({ open: false })}
        onPhotosSelected={handleGooglePhotosSelected}
        onPhotosImported={handleGooglePhotosImported}
        currentSession={currentSession}
      />

      {/* Scene Tagging Dialog */}
      <SceneTaggingDialogImproved
        open={sceneTaggingDialog.open}
        onClose={() => setSceneTaggingDialog({ open: false })}
        sessionId={currentSession?.uniqueId}
        sessionName={currentSession?.name}
        images={images} // Pass all available images for selection
        onSceneTaggingComplete={(results) => {
          console.log('Scene tagging completed:', results);
          // Refresh images to show updated scene data
          loadImages();
        }}
      />

      {/* Image Similarity Dialog */}
      <ImageSimilarityDialog
        key={`similarity-${selectedImageIds.join(',')}`}
        open={similarityDialog.open}
        onClose={() => setSimilarityDialog({ open: false })}
        sessionId={currentSession?.uniqueId}
        eventType={currentSession?.settings?.eventType}
        selectedImages={images.filter(img => selectedImageIds.includes(img.uniqueId || img._id))}
        selectedCount={selectedImageIds.length}

      />
    </Box>
  );
};

export default ImageUploadPage;
